/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import update from "immutability-helper"
import { compact, orderBy, find, omit, merge } from "lodash"
import {
  Menu,
  Category,
  Section,
  Item,
  ModifierGroup,
  Department,
  SubDepartment,
  OutletStore,
  SellingTime,
  MenuGroup,
  ItemStock,
  GrabCategory,
  PostPredictionFeedbacksGrabCategory,
  MerchantStoreInfo,
  MenuTranslationRequestData,
  AttributeCluster,
  TranslationSetting,
  VerifyTranslationRequestData,
  TranslationFailedResponse,
  Modifier,
  TranslationDetail,
  BusinessType,
} from "types/models/menu"
import {
  AxiosAction,
  CreateSectionRequest,
  CreateCategoryRequest,
  UpdateSectionRequest,
  UpdateCategoryRequest,
  FetchMenuResponse,
  FetchDepartmentsResponse,
  FetchStoreListResponse,
  FetchMerchantDetailResponse,
  ResponseWithError,
  FetchMenuGroupListResponse,
  FetchMenuGroupLinkedStoresResponse,
  FetchSuggestedDepartmentResponse,
  FetchMerchantAggregateResponse,
  FetchItemsWithoutPhotoCountResponse,
  GetAlcoholPredictionResponse,
  FetchTranslationResponse,
  FetchListItemAttributesResponse,
  SearchItemsResponse,
} from "types/api"
import {
  getSellingTimeList,
  getCategoryList,
  getItemList,
  getMartItemList,
  getMartSubDepartmentList,
  mapItemClassToDepartment,
  filterDepartmentsForGrabCategory,
  filteredSubDepartmentsForGrabCategory,
} from "utils/menu_helper"
import {
  isMenuStoreVisible,
  getIsSellingTimeStructureV2,
  isMartStoreV2,
  isMartDepartmentV2Structure,
} from "utils/menu_store_helper"
import { getApiUrl, getPortalUrl } from "constants/domain"
import { EventAction, EventCategory } from "constants/tracking"
import { getLinkedModifierGroups, unifyHiddenStatus } from "utils/item_helper"
import { TroyWebTracker } from "utils/tracking"
import {
  FetchListAttributeOptionsResponse,
  FetchSupportedAttributeClusterResponse,
} from "types/api/menuFetchAttributeOptions.type"
import { CreateSellingTimeRequest } from "types/api/menuCreateSellingTime"
import { UpdateSellingTimeRequest } from "types/api/menuUpdateSellingTime"
import { UpdateDepartmentRequest } from "types/api/menuUpdateDepartment"
import {
  DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION,
  DEFAULT_PAGE_SIZE_BRAND_NAME,
  emptyModifierGroup,
  initialModifiers,
} from "constants/menu"
import { ItemFilterType } from "components/dropdowns/ItemPhotoSelect"
import {
  ItemValidationRequest,
  UpsertItemV2Request,
} from "types/api/menuUpdateItem.type"
import {
  generateAutoTranslationQueueKeys,
  generateTranslationFromDSKey,
  getItemTranslationErrorKey,
} from "utils/translation_helper"
import {
  handleSelectOverviewCategory,
  handleSelectOverviewDepartment,
  handleSelectOverviewSection,
  handleSelectOverviewSellingTime,
  handleSelectOverviewSubDepartment,
  handleMultiSelectItems,
  handleMultiSelectCategory,
  handleMultiSelectDepartment,
  handleMultiSelectSubDepartment,
  handleSelectSectionByID,
  handleSelectCategoryByID,
  handleSelectDepartmentByID,
  handleSelectSubDepartmentByID,
  handleSelectItemByID,
} from "./handlers/menuOverview"
import { handleUpdateEditAllowed } from "./handlers/menuState"
import { createReducer } from "../create-reducer"
import {
  MenuState,
  MenuDrawerState,
  SortableResource,
  MenuModalState,
  ItemPrediction,
  EnhanceImageTask,
} from "./menu.type"
import { initialMenu, initialMenuGroup, initialState } from "./states/menuState"

import { ServiceBasedMenuConfig } from "./global.type"

const NAMESPACE = "MENU"

// actions
export const actions = {
  ITEM_FILTER: `${NAMESPACE}/ITEM_FILTER`,
  TOGGLE_ITEM_PHOTO_COVERAGE_BANNER: `${NAMESPACE}/TOGGLE_ITEM_PHOTO_COVERAGE_BANNER`,
  CLEAR_MENU: `${NAMESPACE}/CLEAR_MENU`,
  FETCH_MENU: `${NAMESPACE}/FETCH_MENU`,
  FETCH_CATALOGUE: `${NAMESPACE}/FETCH_CATALOGUE`,
  FETCH_DEPARTMENTS: `${NAMESPACE}/FETCH_DEPARTMENTS`,
  FETCH_SUBDEPARTMENTS: `${NAMESPACE}/FETCH_SUBDEPARTMENTS`,
  UPLOAD_ITEM_PHOTO: `${NAMESPACE}/UPLOAD_ITEM_PHOTO`,
  ENHANCE_IMAGE: `${NAMESPACE}/ENHANCE_IMAGE`,
  GET_ITEM_PREDICTION: `${NAMESPACE}/GET_ITEM_PREDICTION`,
  SET_ALCOHOL_SETTING: `${NAMESPACE}/SET_ALCOHOL_SETTING`,
  SET_BCRS_MENU_GROUP_V2: `${NAMESPACE}/SET_BCRS_MENU_GROUP_V2`,
  SET_TOBACCO_SETTING: `${NAMESPACE}/SET_TOBACCO_SETTING`,
  CREATE_SECTION: `${NAMESPACE}/CREATE_SECTION`,
  UPDATE_SECTION: `${NAMESPACE}/UPDATE_SECTION`,
  DELETE_SECTION: `${NAMESPACE}/DELETE_SECTION`,
  CREATE_CATEGORY: `${NAMESPACE}/CREATE_CATEGORY`,
  UPDATE_CATEGORY: `${NAMESPACE}/UPDATE_CATEGORY`,
  DELETE_CATEGORY: `${NAMESPACE}/DELETE_CATEGORY`,
  SORT_CATEGORIES: `${NAMESPACE}/SORT_CATEGORIES`,
  UPDATE_DEPARTMENT: `${NAMESPACE}/UPDATE_DEPARTMENT`,
  SORT_DEPARTMENTS: `${NAMESPACE}/SORT_DEPARTMENTS`,
  UPDATE_ITEM_AVAILABLE_STATUS: `${NAMESPACE}/UPDATE_ITEM_AVAILABLE_STATUS`,
  SYNC_SEARCH_ITEMS: `${NAMESPACE}/SYNC_SEARCH_ITEMS`,
  UPDATE_ITEMS_SELLING_TIME: `${NAMESPACE}/UPDATE_ITEMS_SELLING_TIME`,
  DELETE_ITEM: `${NAMESPACE}/DELETE_ITEM`,
  SORT_ITEMS: `${NAMESPACE}/SORT_ITEMS`,
  SORT_MENU_ENTITIES: `${NAMESPACE}/SORT_MENU_ENTITIES`,
  CLEAR_STORE: `${NAMESPACE}/CLEAR_STORE`,
  FETCH_CITY_LIST: `${NAMESPACE}/FETCH_CITY_LIST`,
  FETCH_ITEMS_WITHOUT_PHOTO_COUNT: `${NAMESPACE}/FETCH_ITEMS_WITHOUT_PHOTO_COUNT`,
  FETCH_MERCHANT_BUSINESS_TYPE_LIST: `${NAMESPACE}/FETCH_MERCHANT_BUSINESS_TYPE_LIST`,
  FETCH_MERCHANT_AGGREGATE: `${NAMESPACE}/FETCH_MERCHANT_AGGREGATE`,
  SEARCH_STORE: `${NAMESPACE}/SEARCH_STORE`,
  FETCH_STORE_LIST: `${NAMESPACE}/FETCH_STORE_LIST`,
  FETCH_MERCHANT_DETAIL: `${NAMESPACE}/FETCH_MERCHANT_DETAIL`,
  FILTER_ITEM_ID: `${NAMESPACE}/FILTER_ITEM_ID`,
  SET_SEARCH_TEXT: `${NAMESPACE}/SET_SEARCH_TEXT`,
  TOGGLE_DRAWER: `${NAMESPACE}/TOGGLE_DRAWER`,
  TOGGLE_MODAL: `${NAMESPACE}/TOGGLE_MODAL`,
  SELECT_SECTION: `${NAMESPACE}/SELECT_SECTION`,
  SELECT_SELLING_TIME: `${NAMESPACE}/SELECT_SELLING_TIME`,
  SELECT_CATEGORY: `${NAMESPACE}/SELECT_CATEGORY`,
  SELECT_DEPARTMENT: `${NAMESPACE}/SELECT_DEPARTMENT`,
  SELECT_SUB_DEPARTMENT: `${NAMESPACE}/SELECT_SUB_DEPARTMENT`,
  SELECT_SECTION_BY_ID: `${NAMESPACE}/SELECT_SECTION_BY_ID`,
  SELECT_CATEGORY_BY_ID: `${NAMESPACE}/SELECT_CATEGORY_BY_ID`,
  SELECT_DEPARTMENT_BY_ID: `${NAMESPACE}/SELECT_DEPARTMENT_BY_ID`,
  SELECT_SUB_DEPARTMENT_BY_ID: `${NAMESPACE}/SELECT_SUB_DEPARTMENT_BY_ID`,
  SELECT_ITEM_BY_ID: `${NAMESPACE}/SELECT_ITEM_BY_ID`,
  SEARCH_MODIFIER_GROUP: `${NAMESPACE}/SEARCH_MODIFIER_GROUP`,
  SEARCH_MODIFIER_GROUP_BY_ID: `${NAMESPACE}/SEARCH_MODIFIER_GROUP_BY_ID`,
  MULTI_SELECT_DEPARTMENT: `${NAMESPACE}/MULTI_SELECT_DEPARTMENT`,
  MULTI_SELECT_SUB_DEPARTMENT: `${NAMESPACE}/MULTI_SELECT_SUB_DEPARTMENT`,
  MULTI_SELECT_CATEGORY: `${NAMESPACE}/MULTI_SELECT_CATEGORY`,
  MULTI_SELECT_ITEM: `${NAMESPACE}/MULTI_SELECT_ITEM`,
  INIT_LINKED_MODIFIERGROUP: `${NAMESPACE}/INIT_LINKED_MODIFIERGROUP`,
  UPDATE_LINKED_MODIFIERGROUP: `${NAMESPACE}/UPDATE_LINKED_MODIFIERGROUP`,
  REARRANGE_LINKED_MODIFIERGROUP: `${NAMESPACE}/REARRANGE_LINKED_MODIFIERGROUP`,
  FETCH_LIST_BRANDS: `${NAMESPACE}/FETCH_LIST_BRANDS`,
  FETCH_LIST_BRANDS_SEARCH: `${NAMESPACE}/FETCH_LIST_BRANDS_SEARCH`,
  FETCH_ATTRIBUTE_CLUSTER_OPTION: `${NAMESPACE}/FETCH_ATTRIBUTE_CLUSTER_OPTION`,
  FETCH_SUPPORTED_ATTRIBUTE_CLUSTER: `${NAMESPACE}/FETCH_SUPPORTED_ATTRIBUTE_CLUSTER`,
  CREATE_SELLING_TIME: `${NAMESPACE}/CREATE_SELLING_TIME`,
  UPDATE_SELLING_TIME: `${NAMESPACE}/UPDATE_SELLING_TIME`,
  DELETE_SELLING_TIME: `${NAMESPACE}/DELETE_SELLING_TIME`,
  FETCH_MENU_GROUP_LIST: `${NAMESPACE}/FETCH_MENU_GROUP_LIST`,
  CLEAR_MENU_GROUP: `${NAMESPACE}/CLEAR_MENU_GROUP`,
  FETCH_MENU_GROUP_DETAIL: `${NAMESPACE}/FETCH_MENU_GROUP_DETAIL`,
  DELETE_MENU_GROUP: `${NAMESPACE}/DELETE_MENU_GROUP`,
  FETCH_MENU_GROUP_LINKED_STORES: `${NAMESPACE}/FETCH_MENU_GROUP_LINKED_STORES`,
  CREATE_MENU_GROUP: `${NAMESPACE}/CREATE_MENU_GROUP`,
  PUBLISH_MENU_GROUP: `${NAMESPACE}/PUBLISH_MENU_GROUP`,
  RESET_MENU_GROUP: `${NAMESPACE}/RESET_MENU_GROUP`,
  UPDATE_MENU_GROUP: `${NAMESPACE}/UPDATE_MENU_GROUP`,
  UNLINK_STORE_FROM_MENU_GROUP: `${NAMESPACE}/UNLINK_STORE_FROM_MENU_GROUP`,
  LINK_STORE_FROM_MENU_GROUP: `${NAMESPACE}/LINK_STORE_FROM_MENU_GROUP`,
  LINK_STORE_TO_MENU_GROUP: `${NAMESPACE}/LINK_STORE_TO_MENU_GROUP`,
  UPSERT_ITEM_STOCK: `${NAMESPACE}/UPSERT_ITEM_STOCK`,
  FETCH_SUGGESTED_DEPARTMENT: `${NAMESPACE}/FETCH_SUGGESTED_DEPARTMENT`,
  FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY: `${NAMESPACE}/FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY`,
  LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY: `${NAMESPACE}/LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY`,
  FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY: `${NAMESPACE}/FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY`,
  GET_SELECTED_GRAB_CATEGORY: `${NAMESPACE}/GET_SELECTED_GRAB_CATEGORY`,
  POST_FEEDBACKS_GRAB_CATEGORY: `${NAMESPACE}/POST_FEEDBACKS_GRAB_CATEGORY`,
  GET_TRANSLATION: `${NAMESPACE}/GET_TRANSLATION`,
  GET_SINGLE_TRANSLATION: `${NAMESPACE}/GET_SINGLE_TRANSLATION`,
  UPDATE_EXISTING_TRANSLATION: `${NAMESPACE}/UPDATE_EXISTING_TRANSLATION`,
  POST_VERIFY_TRANSLATION: `${NAMESPACE}/POST_VERIFY_TRANSLATION`,
  RESET_AUTO_TRANSLATION_QUEUE: `${NAMESPACE}/RESET_AUTO_TRANSLATION_QUEUE`,
  CLEAR_SELECTED_MODIFIER: `${NAMESPACE}/CLEAR_SELECTED_MODIFIER`,
  SET_ENABLE_MENU_TRANSLATION_PORTAL: `${NAMESPACE}/SET_ENABLE_MENU_TRANSLATION_PORTAL`,
  SET_MODIFIER_NAME_TRANSLATION: `${NAMESPACE}/SET_MODIFIER_NAME_TRANSLATION`,
  SET_MODIFIER_GROUP_NAME_TRANSLATION: `${NAMESPACE}/SET_MODIFIER_GROUP_NAME_TRANSLATION`,
  REMOVE_TRANSLATION_ERROR: `${NAMESPACE}/REMOVE_TRANSLATION_ERROR`,
  SET_ENABLE_PARTIAL_MENU: `${NAMESPACE}/SET_ENABLE_PARTIAL_MENU`,
  SET_ENABLE_GRAB_MEX_PRO_IMAGE_EDITOR: `${NAMESPACE}/SET_ENABLE_GRAB_MEX_PRO_IMAGE_EDITOR`,
  ITEM_VALIDATION: `${NAMESPACE}/ITEM_VALIDATION`,
  UPSERT_ITEM_V2: `${NAMESPACE}/UPSERT_ITEM_V2`,
  SEARCH_ITEMS: `${NAMESPACE}/SEARCH_ITEMS`,
  CLEAR_SEARCH_RESULTS: `${NAMESPACE}/CLEAR_SEARCH_RESULTS`,
}

const apiUrl = getApiUrl()
const portalUrl = getPortalUrl()
const baseUrl = `${apiUrl}/food/merchant`

// getTranslation, getSingleTranslation and updateExistingTranslation post the
// same menu-translations request; only the action type (and error surfacing)
// differs between them
const createTranslationRequestAction = <T extends string>(
  type: T,
  showError = false,
) => ({
  data,
  merchantID,
  merchantGroupID,
  menuGroupID,
}: {
  data: MenuTranslationRequestData
  merchantID: string
  merchantGroupID: string
  menuGroupID?: string
}) => ({
  type,
  params: data,
  keys: generateAutoTranslationQueueKeys(data),
  payload: {
    request: {
      url: `${baseUrl}/v1${
        menuGroupID ? "/menu-groups" : ""
      }/menu-translations`,
      method: "post",
      data: {
        ...data,
        ...(!!menuGroupID && { menuGroupID }),
      },
      headers: {
        merchantID,
        merchantGroupID,
      },
      timeout: 30000,
    },
  },
  ...(showError && { showError: true }),
})

// action creators
export const actionCreator = {
  itemFilter: (value: ItemFilterType) => ({
    type: actions.ITEM_FILTER,
    payload: {
      value,
    },
  }),
  toggleItemPhotoCoverageBanner: (value: boolean) => ({
    type: actions.TOGGLE_ITEM_PHOTO_COVERAGE_BANNER,
    payload: {
      value,
    },
  }),
  clearMenu: () => ({
    type: actions.CLEAR_MENU,
  }),
  fetchMenu: ({
    merchantID,
    menuGroupID,
    merchantGroupID,
    showGlobalLoading,
    isRefreshing = false,
    isSellingTimeStructure = false,
  }: {
    merchantID?: string
    menuGroupID?: string // if is menu group
    merchantGroupID?: string
    showGlobalLoading?: boolean
    isRefreshing?: boolean
    isSellingTimeStructure?: boolean
  }) => ({
    type: actions.FETCH_MENU,
    showGlobalLoading,
    isRefreshing,
    showError: true,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${isSellingTimeStructure ? "v2" : "v1"}/${
          menuGroupID ? "menu-groups/" : ""
        }menu`,
        // keep it temporarily for mock
        // url:'http://127.0.0.1:4523/m1/2540970-0-default/food/merchant/v1/menu',
        method: "get",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchCatalogue: ({
    merchantID,
    menuGroupID,
    merchantGroupID,
    showGlobalLoading,
    isRefreshing = true,
    errorCallback,
    isSellingTimeStructure = false,
  }: {
    merchantID?: string
    menuGroupID?: string // if is menu group
    merchantGroupID?: string
    showGlobalLoading?: boolean
    isRefreshing?: boolean
    errorCallback?(response: ResponseWithError): any
    isSellingTimeStructure?: boolean
  }) => ({
    type: actions.FETCH_CATALOGUE,
    showGlobalLoading,
    showError: true,
    isRefreshing,
    errorCallback,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${isSellingTimeStructure ? "v2" : "v1"}/${
          menuGroupID ? "menu-groups/" : ""
        }mart-menu`,
        // keep it temporarily for mock
        // url:'http://127.0.0.1:4523/m1/2540970-0-default/food/merchant/v1/menu',
        method: "get",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchDepartments: ({
    merchantID,
    menuGroupID,
    merchantGroupID,
    page,
    searchName = "",
  }: {
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    page?: number
    searchName?: string
  }) => ({
    type: actions.FETCH_DEPARTMENTS,
    showError: true,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups/" : "v2/"
        }item-classes?offset=${page ? (page - 1) * 100 : 0}&name=${searchName}`,
        method: "get",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchSubDepartments: ({
    merchantID,
    departmentID,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    departmentID: string
    merchantGroupID?: string
    menuGroupID?: string
  }) => ({
    type: actions.FETCH_SUBDEPARTMENTS,
    showError: true,
    payload: {
      merchantID,
      departmentID,
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups/" : "v2/"
        }item-classes?parentID=${departmentID}`,
        method: "get",
        headers: {
          merchantID,
          merchantGroupID,
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
      },
    },
  }),
  filterItemID: (itemID?: string | null) => ({
    type: actions.FILTER_ITEM_ID,
    payload: {
      itemID,
    },
  }),
  setSearchText: (searchText?: string) => ({
    type: actions.SET_SEARCH_TEXT,
    payload: {
      searchText,
    },
  }),
  createSection: ({
    merchantID,
    data,
    successMessage,
    menuGroupID,
    merchantGroupID,
    errorCallback,
  }: {
    merchantID: string
    data: CreateSectionRequest
    successMessage: string
    menuGroupID?: string
    merchantGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.CREATE_SECTION,
    showGlobalLoading: true,
    closeDrawers: true,
    showError: true,
    errorCallback,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${menuGroupID ? "menu-groups/" : ""}section`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          ...data,
          menuGroupID,
        },
      },
    },
  }),
  updateSections: ({
    merchantID,
    data,
    successMessage,
    menuGroupID,
    merchantGroupID,
    errorCallback,
  }: {
    merchantID: string
    data: UpdateSectionRequest
    successMessage: string
    menuGroupID?: string
    merchantGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.UPDATE_SECTION,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    errorCallback,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${menuGroupID ? "menu-groups/" : ""}sections`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          ...data,
          menuGroupID,
        },
      },
    },
  }),
  deleteSection: ({
    merchantID,
    sectionID,
    confirmOptions,
    successMessage,
    menuGroupID,
    merchantGroupID,
  }: {
    merchantID: string
    sectionID: string
    section?: Section | null
    confirmOptions: Object
    successMessage: string
    menuGroupID?: string
    merchantGroupID?: string
  }) => ({
    type: actions.DELETE_SECTION,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    showConfirm: true,
    confirmOptions,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu-groups/" : ""
        }section?sectionID=${sectionID}`,
        method: "delete",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          menuGroupID,
          sectionID,
        },
      },
    },
  }),
  createCategory: ({
    merchantID,
    data,
    successOptions,
    menuGroupID,
    merchantGroupID,
    errorCallback,
  }: {
    merchantID: string
    data: CreateCategoryRequest
    successOptions: Object
    menuGroupID?: string
    merchantGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.CREATE_CATEGORY,
    showGlobalLoading: true,
    // refreshMenu: true,
    closeDrawers: true,
    showError: true,
    errorCallback,
    showSuccess: true,
    successOptions,
    payload: {
      merchantID,
      request: {
        url: !menuGroupID
          ? `${baseUrl}/v2/categories`
          : `${baseUrl}/v1/menu-groups/categories`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          ...data,
          menuGroupID,
        },
      },
    },
  }),
  updateCategory: ({
    merchantID,
    categoryID,
    data,
    isSellingTimeStructure,
    successMessage,
    showConfirm,
    confirmOptions,
    menuGroupID,
    merchantGroupID,
    errorCallback,
  }: {
    merchantID: string
    categoryID: string
    data: UpdateCategoryRequest
    isSellingTimeStructure: boolean
    successMessage: string
    showConfirm?: boolean
    confirmOptions?: Object
    menuGroupID?: string
    merchantGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.UPDATE_CATEGORY,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showConfirm,
    confirmOptions,
    errorCallback,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: !menuGroupID
          ? `${baseUrl}/${
              isSellingTimeStructure ? "v3" : "v2"
            }/categories/${categoryID}`
          : `${baseUrl}/${
              isSellingTimeStructure ? "v2" : "v1"
            }/menu-groups/categories/${categoryID}`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          ...data,
          ...(menuGroupID && { menuGroupID }),
        },
      },
    },
  }),
  deleteCategory: ({
    merchantID,
    categoryID,
    confirmOptions,
    successMessage,
    menuGroupID,
    merchantGroupID,
  }: {
    merchantID: string
    categoryID: string
    confirmOptions?: Object
    successMessage: string
    menuGroupID?: string
    merchantGroupID?: string
  }) => ({
    type: actions.DELETE_CATEGORY,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    showConfirm: true,
    confirmOptions,
    payload: {
      merchantID,
      request: {
        url: !menuGroupID
          ? `${baseUrl}/v2/categories/${categoryID}`
          : `${baseUrl}/v1/menu-groups/categories/${categoryID}`,
        method: "delete",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          menuGroupID,
          categoryID,
        },
      },
    },
  }),
  updateDepartment: ({
    merchantID,
    menuGroupID,
    merchantGroupID,
    departmentID,
    data,
    successMessage,
    showConfirm,
    confirmOptions,
    errorCallback,
  }: {
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    departmentID: string
    data: UpdateDepartmentRequest
    successMessage: string
    showConfirm?: boolean
    confirmOptions?: Object
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.UPDATE_DEPARTMENT,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showConfirm,
    confirmOptions,
    errorCallback,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: !menuGroupID
          ? `${baseUrl}/v1/departments/${departmentID}`
          : `${baseUrl}/v1/menu-groups/departments/${departmentID}`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data,
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
      },
    },
  }),
  itemValidation: ({
    merchantID,
    data,
    merchantGroupID,
    menuGroupID,
    errorCallback,
  }: {
    merchantID: string
    data: ItemValidationRequest
    merchantGroupID?: string
    menuGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.ITEM_VALIDATION,
    showGlobalLoading: false,
    closeDrawers: false,
    showError: true,
    errorCallback,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v2/menu-groups" : "v2"
        }/item-validation`,
        method: "post",
        headers: {
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        },
        data: {
          item: data.item,
          categoryID: data.categoryID,
          ...(!!menuGroupID && { menuGroupID }),
        },
      },
    },
  }),
  upsertItemV2: ({
    merchantID,
    data,
    merchantGroupID,
    menuGroupID,
    errorCallback,
    closeDrawers = true,
    showGlobalLoading = true,
  }: {
    merchantID: string
    data: UpsertItemV2Request
    merchantGroupID?: string
    menuGroupID?: string
    errorCallback?(response: ResponseWithError): any
    closeDrawers?: boolean
    showGlobalLoading?: boolean
  }) => ({
    type: actions.UPSERT_ITEM_V2,
    showGlobalLoading,
    closeDrawers,
    showError: true,
    errorCallback,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${menuGroupID ? "v2/menu-groups" : "v2"}/upsert-item`,
        method: "post",
        headers: {
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        },
        data: {
          item: data.item,
          categoryID: data.categoryID,
          itemAttributeValues: data.newAttributes,
          ...(!!menuGroupID && { menuGroupID }),
        },
      },
    },
  }),
  searchItems: ({
    merchantID,
    merchantGroupID,
    searchKey,
    errorCallback,
    showLoading = true,
  }: {
    merchantID?: string
    merchantGroupID?: string
    searchKey: string
    errorCallback?(response: ResponseWithError): any
    showLoading?: boolean
  }) => ({
    type: actions.SEARCH_ITEMS,
    showError: true,
    showLoading, // Pass to reducer to control searchItemLoading state
    errorCallback,
    payload: {
      request: {
        url: `${baseUrl}/v1/search-items`,
        method: "get",
        params: {
          searchKey,
          needClassItemsInfo: true,
          needCategoryInfo: true,
          needStock: true,
          needModifierGroups: true,
        },
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  clearSearchResults: () => ({
    type: actions.CLEAR_SEARCH_RESULTS,
  }),
  updateItemsAvailableStatus: ({
    merchantID,
    itemIDs,
    availableStatus,
    successMessage,
    departmentIDs,
    subDepartmentIDs,
    excludedSubDepartmentIDs,
    excludedItemIDs,
  }: {
    merchantID: string
    itemIDs?: string[]
    availableStatus: number
    successMessage?: string
    departmentIDs?: string[]
    subDepartmentIDs?: string[]
    excludedSubDepartmentIDs?: string[]
    excludedItemIDs?: string[]
  }) => ({
    type: actions.UPDATE_ITEM_AVAILABLE_STATUS,
    showGlobalLoading: true,
    refreshMenu: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/items/available-status`,
        method: "put",
        headers: {
          merchantID,
        },
        data: {
          itemIDs,
          availableStatus,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
        },
      },
    },
  }),
  updateItemsSellingTime: ({
    merchantID,
    menuGroupID,
    merchantGroupID,
    itemIDs,
    departmentIDs,
    subDepartmentIDs,
    excludedSubDepartmentIDs,
    excludedItemIDs,
    sellingTimeID,
    successMessage,
  }: {
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    itemIDs?: string[]
    departmentIDs?: string[]
    subDepartmentIDs?: string[]
    excludedSubDepartmentIDs?: string[]
    excludedItemIDs?: string[]
    sellingTimeID: string
    successMessage?: string
  }) => ({
    type: actions.UPDATE_ITEMS_SELLING_TIME,
    showGlobalLoading: true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/items/sellingTime`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          itemIDs,
          departmentIDs,
          subDepartmentIDs,
          excludedSubDepartmentIDs,
          excludedItemIDs,
          item: {
            sellingTimeID,
          },
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
      },
    },
  }),
  deleteItem: ({
    merchantID,
    itemID,
    showConfirm,
    showSuccess,
    successMessage,
    showGlobalLoading,
    confirmOptions,
    merchantGroupID,
    menuGroupID,
    errorCallback,
  }: {
    merchantID: string
    itemID: string
    showConfirm?: boolean
    showSuccess?: boolean
    successMessage?: string
    showGlobalLoading?: boolean
    confirmOptions?: Object
    merchantGroupID?: string
    menuGroupID?: string
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.DELETE_ITEM,
    showGlobalLoading: showGlobalLoading ?? true,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    errorCallback,
    showSuccess: showSuccess ?? true,
    successOptions: {
      message: successMessage,
    },
    showConfirm: showConfirm ?? true,
    confirmOptions,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/items/${itemID}`,
        method: "delete",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          itemID,
          menuGroupID,
        },
      },
    },
  }),
  sortCategories: ({
    merchantID,
    sectionID,
    sorts,
    successMessage,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    sectionID: string
    sorts: SortableResource[]
    successMessage: string
    merchantGroupID?: string
    menuGroupID?: string
  }) => ({
    type: actions.SORT_CATEGORIES,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups/" : ""
        }categories-sort`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          sectionSorts: [
            {
              sectionID,
              sorts,
            },
          ],
          menuGroupID,
        },
      },
    },
  }),
  sortDepartments: ({
    merchantID,
    sorts,
    parentDepartmentID = null,
    successMessage,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    sorts: SortableResource[]
    parentDepartmentID?: string | null
    successMessage: string
    menuGroupID?: string
    merchantGroupID?: string
  }) => ({
    type: actions.SORT_DEPARTMENTS,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups/" : ""
        }departments-sort`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          parentDepartmentID,
          sorts,
          menuGroupID,
        },
      },
    },
  }),
  sortDepartmentItems: ({
    merchantID,
    sorts,
    departmentID,
    successMessage,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    sorts: SortableResource[]
    departmentID: string
    successMessage: string
    merchantGroupID?: string
    menuGroupID?: string
  }) => ({
    type: actions.SORT_ITEMS,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups/" : ""
        }departments/items-sort`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          departmentID,
          sorts,
          menuGroupID,
        },
      },
    },
  }),
  sortItems: ({
    merchantID,
    categoryID,
    sorts,
    successMessage,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    categoryID: string
    sorts: SortableResource[]
    successMessage: string
    merchantGroupID?: string
    menuGroupID?: string
  }) => ({
    type: actions.SORT_ITEMS,
    refreshMenu: true,
    closeDrawers: true,
    showError: true,
    showSuccess: true,
    successOptions: {
      message: successMessage,
    },
    payload: {
      request: {
        url: `${baseUrl}/${menuGroupID ? "v1/menu-groups/" : ""}items-sort`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          categoryID,
          sorts,
          menuGroupID,
        },
      },
    },
  }),
  sortMenuEntities: ({
    merchantID,
    parentEntityID,
    parentEntityType,
    sorts,
    sortToEntityType,
    merchantGroupID,
    menuGroupID,
  }: {
    merchantID: string
    parentEntityID: string
    parentEntityType: number
    sorts: SortableResource[]
    sortToEntityType: number
    merchantGroupID?: string
    menuGroupID?: string
  }) => ({
    type: actions.SORT_MENU_ENTITIES,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu-groups/" : ""
        }menu-entities/sort-order`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          menuGroupID,
          parentEntityID,
          parentEntityType,
          sorts,
          sortToEntityType,
        },
      },
    },
  }),
  uploadItemPhoto: (
    photo: string,
    type: string,
    merchantID: string,
    merchantGroupID?: string,
    menuGroupID?: string,
  ) => ({
    type: actions.UPLOAD_ITEM_PHOTO,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/upload-menu-group-file" : "v2/upload-file"
        }`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          file: {
            data: photo,
            type,
          },
          category: "menu_item_img",
          menuGroupID,
        },
      },
      data: {
        url: "",
        metadata: {
          detection: {
            errorItems: [],
          },
        },
      },
    },
  }),
  enhanceImage: ({
    image,
    task,
    merchantID,
    merchantGroupID,
  }: {
    image: string
    task: EnhanceImageTask
    merchantID: string
    merchantGroupID?: string
  }) => ({
    type: actions.ENHANCE_IMAGE,
    payload: {
      request: {
        url: `${baseUrl}/v1/enhance-image`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
        },
        data: {
          image,
          task,
        },
      },
    },
  }),
  getItemPrediction: ({
    data: { name, desc, photos },
    merchantID,
    merchantGroupID,
    isMenuGroup,
    menuGroupID,
  }: {
    data: ItemPrediction
    merchantID: string
    merchantGroupID?: string
    isMenuGroup: boolean
    menuGroupID?: string
  }) => {
    const params = new URLSearchParams({ name, desc })
    photos.forEach(url => params.append("photos", url))

    return {
      type: actions.GET_ITEM_PREDICTION,
      showError: true,
      showGlobalLoading: true,
      payload: {
        request: {
          url: `${baseUrl}/v1/${
            isMenuGroup
              ? "menu-groups/items/item-prediction"
              : "item-prediction"
          }`,
          method: "get",
          params,
          headers: {
            merchantID: merchantID || menuGroupID,
            merchantGroupID,
          },
        },
      },
    }
  },
  toggleDrawer: (drawer: MenuDrawerState) => ({
    type: actions.TOGGLE_DRAWER,
    payload: { drawer },
  }),
  toggleModal: (modal: MenuModalState) => ({
    type: actions.TOGGLE_MODAL,
    payload: { modal },
  }),
  selectSection: (section: Section) => ({
    type: actions.SELECT_SECTION,
    payload: {
      section,
    },
  }),
  selectSellingTime: (sellingTime?: SellingTime) => ({
    type: actions.SELECT_SELLING_TIME,
    payload: {
      sellingTime,
    },
  }),
  selectCategory: (category: Category) => ({
    type: actions.SELECT_CATEGORY,
    payload: {
      category,
    },
  }),
  selectDepartment: (department: Department | null) => ({
    type: actions.SELECT_DEPARTMENT,
    payload: {
      department,
    },
  }),
  selectSubDepartment: (subDepartment: SubDepartment | null) => ({
    type: actions.SELECT_SUB_DEPARTMENT,
    payload: {
      subDepartment,
    },
  }),
  selectSectionByID: (sectionID: string) => ({
    type: actions.SELECT_SECTION_BY_ID,
    payload: {
      sectionID,
    },
  }),
  selectCategoryByID: (categoryID: string) => ({
    type: actions.SELECT_CATEGORY_BY_ID,
    payload: {
      categoryID,
    },
  }),
  selectSubDepartmentByID: (subDepartmentID: string) => ({
    type: actions.SELECT_SUB_DEPARTMENT_BY_ID,
    payload: {
      subDepartmentID,
    },
  }),
  selectDepartmentByID: (departmentID: string) => ({
    type: actions.SELECT_DEPARTMENT_BY_ID,
    payload: {
      departmentID,
    },
  }),
  selectItemByID: (itemID: string) => ({
    type: actions.SELECT_ITEM_BY_ID,
    payload: {
      itemID,
    },
  }),
  multiSelectDepartment: (department: Department, value: boolean) => ({
    type: actions.MULTI_SELECT_DEPARTMENT,
    payload: {
      department,
      value,
    },
  }),
  multiSelectSubDepartment: (subDepartment: SubDepartment, value: boolean) => ({
    type: actions.MULTI_SELECT_SUB_DEPARTMENT,
    payload: {
      subDepartment,
      value,
    },
  }),
  multiSelectCategory: (category: Category, value: boolean) => ({
    type: actions.MULTI_SELECT_CATEGORY,
    payload: {
      category,
      value,
    },
  }),
  multiSelectItem: (item: Item, value: boolean) => ({
    type: actions.MULTI_SELECT_ITEM,
    payload: {
      item,
      value,
    },
  }),
  searchModifierGroup: (keyword: string) => ({
    type: actions.SEARCH_MODIFIER_GROUP,
    payload: {
      keyword,
    },
  }),
  searchModifierGroupByID: (modifierGroupID: string) => ({
    type: actions.SEARCH_MODIFIER_GROUP_BY_ID,
    payload: {
      modifierGroupID,
    },
  }),
  fetchStoreList: ({
    countryCode,
    offset,
    limit,
    append = false,
    isWithItemPhotoCount = false,
    stopAfterFirstPage = false,
  }: {
    countryCode: string
    offset: number
    limit: number
    append: boolean
    isWithItemPhotoCount?: boolean
    stopAfterFirstPage?: boolean
  }) => ({
    type: actions.FETCH_STORE_LIST,
    notTrackError: true, // trackError middleware exception
    payload: {
      append,
      stopAfterFirstPage,
      request: {
        url: `${portalUrl}/foodtroy/v1/${countryCode}/merchant-groups/catalog-stores?offset=${offset}&limit=${limit}&isWithItemPhotoCount=${isWithItemPhotoCount}`,
        method: "get",
      },
    },
  }),
  searchOutlets: ({
    search = "",
    offset = 0,
    limit = 100,
    includeItemsWithoutPhotosCount = false,
    city = [],
    asc = true,
    modelType = "integrated",
    isLoadMore,
    deliverOption = [],
    businessType = [],
    includeMenuGroupV2ID = false,
  }: {
    search?: string
    offset?: number
    limit?: number
    includeItemsWithoutPhotosCount?: boolean
    isFetchFeatureFlag?: boolean
    city?: string[]
    asc?: boolean
    modelType?: string
    isLoadMore?: boolean
    deliverOption?: string[]
    businessType?: BusinessType[]
    includeMenuGroupV2ID?: boolean
  }) => {
    return {
      type: actions.SEARCH_STORE,
      payload: {
        isLoadMore,
        request: {
          url: `${apiUrl}/delvplatformapi/merchant/v1/merchant-group/store/search`,
          method: "get",
          params: {
            offset,
            limit,
            search,
            includeItemsWithoutPhotosCount,
            includeInactive: true,
            modelType,
            asc,
            cityIDs: city,
            businessTypes: businessType,
            deliverOptionsOriginal: deliverOption,
            includeMenuGroupV2ID,
          },
        },
      },
    }
  },
  fetchItemsWithoutPhotoCount: () => ({
    type: actions.FETCH_ITEMS_WITHOUT_PHOTO_COUNT,
    payload: {
      request: {
        url: `${apiUrl}/delvplatformapi/merchant/v1/merchant-group/store/merchants-items-without-photo-count`,
        method: "get",
      },
    },
  }),
  fetchMerchantAggregate: (...fields: string[]) => {
    return {
      type: actions.FETCH_MERCHANT_AGGREGATE,
      payload: {
        request: {
          url: `${apiUrl}/delvplatformapi/merchant/v1/merchant-group/store/aggregate-by-fields`,
          method: "get",
          params: {
            fields: [...fields],
            modelType: "integrated",
            includeInactive: true,
          },
        },
      },
    }
  },
  clearStore: () => ({
    type: actions.CLEAR_STORE,
  }),
  fetchMerchantDetail: ({
    merchantID,
    countryCode,
  }: {
    merchantID: string
    countryCode: string
  }) => ({
    type: actions.FETCH_MERCHANT_DETAIL,
    payload: {
      merchantID,
      request: {
        url: `${portalUrl}/foodtroy/v2/${countryCode}/merchants/${merchantID}`,
        method: "get",
      },
    },
  }),
  initLinkedModifierGroup: (modifierGroups: ModifierGroup[]) => ({
    type: actions.INIT_LINKED_MODIFIERGROUP,
    payload: { modifierGroups },
  }),
  updateLinkedModifierGroup: (modifierGroups: ModifierGroup[]) => ({
    type: actions.UPDATE_LINKED_MODIFIERGROUP,
    payload: { modifierGroups },
  }),
  rearrangeLinkedModifierGroup: (modifierGroups: ModifierGroup[]) => ({
    type: actions.REARRANGE_LINKED_MODIFIERGROUP,
    payload: { modifierGroups },
  }),
  fetchListBrandsSearch: (
    merchantID: string,
    queryParams: {
      locale: string
      page: number
      name: string
    },
  ) => ({
    type: actions.FETCH_LIST_BRANDS_SEARCH,
    payload: {
      request: {
        url: `${baseUrl}/v1/attributes`,
        method: "get",
        params: {
          clusters: "brand",
          pageSize: DEFAULT_PAGE_SIZE_BRAND_NAME,
          ...queryParams,
        },
        headers: {
          merchantID,
        },
      },
    },
  }),
  fetchAttributeClusterOption: (
    merchantID: string,
    merchantGroupID: string,
    isMenuGroup: boolean,
    menuGroupID: string,
    queryParams: {
      attributeClusterIDs: string
      page: number
      name?: string
      locale?: string
    },
  ) => ({
    type: actions.FETCH_ATTRIBUTE_CLUSTER_OPTION,
    attributeClusterID: queryParams.attributeClusterIDs,
    payload: {
      request: {
        url: `${baseUrl}${
          isMenuGroup ? "/v2/menu-groups/attributes" : "/v1/attributes"
        }`,
        method: "get",
        params: {
          pageSize: DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION,
          status: "Enabled",
          ...queryParams,
        },
        headers: {
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchSupportedAttributeCluster: (
    merchantID: string,
    merchantGroupID: string,
    isMenuGroup: boolean,
    menuGroupID: string,
    queryParams: {
      itemClassIDs: string
      countryID: number
      businessType: string
      itemID?: string
    },
  ) => ({
    type: actions.FETCH_SUPPORTED_ATTRIBUTE_CLUSTER,
    payload: {
      request: {
        url: `${baseUrl}${
          isMenuGroup
            ? "/v2/menu-groups/supported-attribute-clusters"
            : "/v1/supported-attribute-clusters"
        }`,
        method: "get",
        params: {
          ...queryParams,
        },
        headers: {
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        },
      },
    },
  }),
  createSellingTime: ({
    data,
    successMessage,
    merchantID,
    menuGroupID,
    merchantGroupID,
    errorCallback,
    closeDrawers = true,
    showGlobalLoading = true,
  }: {
    data: CreateSellingTimeRequest
    successMessage: string
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    errorCallback?(response: ResponseWithError): any
    closeDrawers?: boolean
    showGlobalLoading?: boolean
  }) => ({
    type: actions.CREATE_SELLING_TIME,
    showGlobalLoading,
    closeDrawers,
    showError: true,
    successOptions: {
      message: successMessage,
    },
    showSuccess: true,
    errorCallback,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu-groups" : "menu"
        }/selling-times`,
        method: "post",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
        data,
      },
    },
  }),
  updateSellingTime: ({
    sellingTimeID,
    data,
    successMessage,
    merchantID,
    menuGroupID,
    merchantGroupID,
    errorCallback,
    closeDrawers = true,
    showGlobalLoading = true,
  }: {
    sellingTimeID: string
    data: UpdateSellingTimeRequest
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    successMessage?: string
    errorCallback?(response: ResponseWithError): any
    closeDrawers?: boolean
    showGlobalLoading?: boolean
  }) => ({
    type: actions.UPDATE_SELLING_TIME,
    showGlobalLoading,
    closeDrawers,
    showError: true,
    successOptions: {
      message: successMessage,
    },
    showSuccess: true,
    errorCallback,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu-groups" : "menu"
        }/selling-times/${sellingTimeID}`,
        method: "put",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
        data,
      },
    },
  }),
  deleteSellingTime: ({
    sellingTimeID,
    merchantID,
    menuGroupID,
    merchantGroupID,
    showConfirm,
    showSuccess,
    successMessage,
    showGlobalLoading,
    confirmOptions,
    errorCallback,
  }: {
    sellingTimeID: string
    merchantID?: string
    menuGroupID?: string
    merchantGroupID?: string
    showConfirm?: boolean
    showSuccess?: boolean
    successMessage?: string
    showGlobalLoading?: boolean
    confirmOptions?: Object
    errorCallback?(response: ResponseWithError): any
  }) => ({
    type: actions.DELETE_SELLING_TIME,
    showGlobalLoading: showGlobalLoading ?? true,
    closeDrawers: true,
    showError: true,
    errorCallback,
    showSuccess: showSuccess ?? true,
    successOptions: {
      message: successMessage,
    },
    showConfirm: showConfirm ?? true,
    confirmOptions,
    payload: {
      merchantID,
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu-groups" : "menu"
        }/selling-times/${sellingTimeID}`,
        method: "delete",
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  fetchMenuGroupList: ({
    merchantGroupID,
    menuGroupName,
    isWithItemPhotoCount = false,
  }: {
    merchantGroupID: string
    menuGroupName?: string
    isWithItemPhotoCount?: boolean
  }) => ({
    showGlobalLoading: true,
    showError: true,
    type: actions.FETCH_MENU_GROUP_LIST,
    payload: {
      append: false,
      request: {
        url: `${baseUrl}/v1/menu-groups${
          menuGroupName ? `?menuGroupName=${menuGroupName}&` : "?"
        }isWithItemPhotoCount=${isWithItemPhotoCount}`,
        method: "get",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroupName,
        },
      },
    },
  }),
  clearMenuGroup: () => ({
    type: actions.CLEAR_MENU_GROUP,
  }),
  fetchMenuGroupDetail: ({
    menuGroupID,
    merchantGroupID,
  }: {
    menuGroupID: string
    merchantGroupID: string
  }) => ({
    showGlobalLoading: false,
    type: actions.FETCH_MENU_GROUP_DETAIL,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}`,
        method: "get",
        headers: {
          merchantGroupID,
        },
      },
    },
  }),
  deleteMenuGroup: ({
    menuGroupID,
    merchantGroupID,
  }: {
    menuGroupID: string
    merchantGroupID: string
  }) => ({
    showGlobalLoading: true,
    showError: true,
    showSuccess: true,
    type: actions.DELETE_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/delete`,
        method: "delete",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroupID,
        },
      },
    },
  }),
  fetchMenuGroupLinkedStores: ({
    menuGroupID,
    merchantGroupID,
    page,
    pageSize,
    append = false,
  }: {
    menuGroupID: string
    merchantGroupID: string
    page: number
    pageSize: number
    append: boolean
  }) => ({
    type: actions.FETCH_MENU_GROUP_LINKED_STORES,
    showGlobalLoading: true,
    showError: true,
    payload: {
      append,
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/stores?page=${page}&pageSize=${pageSize}`,
        method: "get",
        headers: {
          merchantGroupID,
        },
      },
    },
  }),
  createMenuGroup: ({
    merchantGroupID,
    menuGroup,
    menuGroupID,
    linkedStoreIDs,
    errorCallback,
  }: {
    merchantGroupID: string
    menuGroup: {
      name: string
      businessType: number
      menuType: 0 | 1 // menu structure: 0 - category based,  1: department based
      timeZone: string
    }
    menuGroupID?: string // existing menu group id
    linkedStoreIDs?: string[]
    errorCallback?: (errorResponse: ResponseWithError) => any
  }) => ({
    showError: true,
    mockFailedResponse: true,
    type: actions.CREATE_MENU_GROUP,
    errorCallback,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups`,
        method: "post",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroup,
          menuGroupID,
          linkedStoreIDs,
        },
      },
    },
  }),
  publishMenuGroup: ({
    merchantGroupID,
    linkedStoreIDs,
    menuGroupID,
  }: {
    merchantGroupID: string
    linkedStoreIDs: string[]
    menuGroupID: string
  }) => ({
    showError: true,
    type: actions.PUBLISH_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/publish`,
        method: "put",
        headers: {
          merchantGroupID,
        },
        data: {
          linkedStoreIDs,
          menuGroupID,
        },
      },
    },
  }),
  resetMenuGroup: ({
    merchantGroupID,
    menuGroupID,
  }: {
    merchantGroupID: string
    menuGroupID: string
  }) => ({
    showError: true,
    type: actions.RESET_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/reset`,
        method: "put",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroupID,
        },
      },
    },
  }),
  updateMenuGroup: ({
    merchantGroupID,
    menuGroupID,
    menuGroupName,
  }: {
    merchantGroupID: string
    menuGroupID: string
    menuGroupName: string
  }) => ({
    showGlobalLoading: true,
    mockFailedResponse: true,
    type: actions.UPDATE_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}`,
        method: "put",
        headers: {
          merchantGroupID,
        },
        data: {
          name: menuGroupName,
        },
      },
    },
  }),
  unlinkStoreFromMenuGroup: ({
    merchantGroupID,
    menuGroupID,
    storeIDs,
    errorCallback,
  }: {
    merchantGroupID: string
    menuGroupID: string
    storeIDs: string[]
    errorCallback?(response: ResponseWithError): any
  }) => ({
    showError: true,
    errorCallback,
    type: actions.UNLINK_STORE_FROM_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/unlink`,
        method: "put",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroupID,
          storeIDs,
        },
      },
    },
  }),
  linkStoreToMenuGroup: ({
    merchantGroupID,
    menuGroupID,
    storeIDs,
    errorCallback,
  }: {
    merchantGroupID: string
    menuGroupID: string
    storeIDs: string[]
    errorCallback?(response: ResponseWithError): boolean | string
  }) => ({
    showError: true,
    notTrackError: true, // trackError middleware exception
    errorCallback,
    type: actions.LINK_STORE_FROM_MENU_GROUP,
    payload: {
      request: {
        url: `${baseUrl}/v1/menu-groups/${menuGroupID}/link`,
        method: "put",
        headers: {
          merchantGroupID,
        },
        data: {
          menuGroupID,
          storeIDs,
        },
      },
    },
  }),
  upsertItemStock: ({
    itemID,
    merchantID,
    itemStock,
    errorCallback,
  }: {
    itemID: string
    merchantID: string
    itemStock: ItemStock
    errorCallback?(response: ResponseWithError): string | undefined
  }) => ({
    showGlobalLoading: true,
    showError: true,
    errorCallback,
    type: actions.UPSERT_ITEM_STOCK,
    payload: {
      request: {
        url: `${baseUrl}/v1/items/${itemID}/upsert-item-stock`,
        method: "post",
        headers: { merchantID },
        data: {
          ...omit(itemStock, ["restockSetting.nextRestockTime"]),
        },
      },
    },
  }),
  mockActions: (actionType: string, data: any, meta: any) => ({
    type: actionType,
    payload: data,
    meta,
  }),
  fetchSuggestedDepartments: ({
    itemName,
    locale = "en",
    menuGroupID,
    merchantGroupID,
    merchantID,
  }: {
    itemName: string
    locale?: string
    menuGroupID?: string
    merchantGroupID?: string
    merchantID?: string
  }) => ({
    type: actions.FETCH_SUGGESTED_DEPARTMENT,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/v1/${
          menuGroupID ? "menu/" : ""
        }items/department-suggestions?itemName=${itemName}${
          menuGroupID ? `&menuGroupID=${menuGroupID}` : ""
        }`,
        method: "get",
        headers: {
          "Accept-Language": locale,
          merchantGroupID,
          merchantID,
        },
      },
    },
  }),
  fetchDepartmentsForGrabCategory: ({
    searchName = "",
    locale = "en",
    menuGroupID,
    merchantGroupID,
    merchantID,
  }: {
    searchName?: string
    locale?: string
    menuGroupID?: string
    merchantGroupID?: string
    merchantID?: string
  }) => ({
    type: actions.FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/item-classes?Name=${searchName}${
          menuGroupID ? `&MenuGroupID=${menuGroupID}` : ""
        }`,
        method: "get",
        headers: {
          merchantGroupID,
          merchantID,
          "Accept-Language": locale,
        },
      },
    },
  }),
  loadMoreDepartmentsForGrabCategory: ({
    searchName = "",
    locale = "en",
    menuGroupID,
    merchantGroupID,
    merchantID,
    offsetGrabCategory = 0,
    isLoadingMore = false,
  }: {
    searchName?: string
    locale?: string
    menuGroupID?: string
    merchantGroupID?: string
    merchantID?: string
    offsetGrabCategory?: number
    isLoadingMore?: boolean
  }) => ({
    type: actions.LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/item-classes?Name=${searchName}${
          menuGroupID ? `&MenuGroupID=${menuGroupID}` : ""
        }&offset=${offsetGrabCategory}`,
        method: "get",
        headers: {
          merchantGroupID,
          merchantID,
          "Accept-Language": locale,
        },
      },
      isLoadingMore,
    },
  }),
  fetchSubdepartmentsForGrabCategory: ({
    parentID = "",
    parentName = "",
    locale = "en",
    menuGroupID,
    merchantGroupID,
    merchantID,
  }: {
    parentID: string
    parentName: string
    locale?: string
    menuGroupID?: string
    merchantGroupID?: string
    merchantID?: string
  }) => ({
    type: actions.FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/item-classes?parentID=${parentID}${
          menuGroupID ? `&MenuGroupID=${menuGroupID}` : ""
        }`,
        method: "get",
        headers: {
          merchantGroupID,
          merchantID,
          "Accept-Language": locale,
        },
      },
      parentName,
    },
  }),
  getSelectedGrabCategory: (data: GrabCategory) => ({
    type: actions.GET_SELECTED_GRAB_CATEGORY,
    payload: data,
  }),
  postFeedbackGrabCategory: (
    data: PostPredictionFeedbacksGrabCategory,
    merchantID: string,
    merchantGroupID: string,
    menuGroupID?: string,
  ) => ({
    type: actions.POST_FEEDBACKS_GRAB_CATEGORY,
    showError: true,
    payload: {
      request: {
        url: `${baseUrl}/v1${menuGroupID ? "/menu" : ""}/prediction-feedbacks`,
        method: "post",
        data: {
          PredictionFeedback: data,
        },
        headers: {
          merchantID: merchantID || menuGroupID,
          merchantGroupID,
        },
      },
    },
  }),
  getTranslation: createTranslationRequestAction(actions.GET_TRANSLATION),
  getSingleTranslation: createTranslationRequestAction(
    actions.GET_SINGLE_TRANSLATION,
    true,
  ),
  updateExistingTranslation: createTranslationRequestAction(
    actions.UPDATE_EXISTING_TRANSLATION,
  ),
  getVerifyTranslation: ({
    data,
    merchantID,
    merchantGroupID,
    menuGroupID,
  }: {
    data: VerifyTranslationRequestData
    merchantID: string
    merchantGroupID: string
    menuGroupID?: string
  }) => ({
    type: actions.POST_VERIFY_TRANSLATION,
    showError: true,
    params: data,
    payload: {
      request: {
        url: `${baseUrl}/v1${
          menuGroupID ? "/menu-groups" : ""
        }/verify-translation`,
        method: "post",
        data: {
          ...data,
          ...(!!menuGroupID && { menuGroupID }),
        },
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  resetAutoTranslationQueue: () => ({
    type: actions.RESET_AUTO_TRANSLATION_QUEUE,
  }),
  setAlcoholSetting: (value: boolean) => ({
    type: actions.SET_ALCOHOL_SETTING,
    payload: { value },
  }),
  setBcrsMenuGroupV2: (value: boolean, isMenuGroupPage: boolean) => ({
    type: actions.SET_BCRS_MENU_GROUP_V2,
    payload: { value, isMenuGroupPage },
  }),
  setTobaccoSetting: (value: boolean) => ({
    type: actions.SET_TOBACCO_SETTING,
    payload: { value },
  }),
  setEnableMenuTranslationPortal: (value: boolean) => ({
    type: actions.SET_ENABLE_MENU_TRANSLATION_PORTAL,
    payload: { value },
  }),
  setEnablePartialMenu: (value: boolean) => ({
    type: actions.SET_ENABLE_PARTIAL_MENU,
    payload: { value },
  }),
  setEnableGrabMexProImageEditor: (value: boolean) => ({
    type: actions.SET_ENABLE_GRAB_MEX_PRO_IMAGE_EDITOR,
    payload: { value },
  }),
  clearSelectedModifier: () => ({
    type: actions.CLEAR_SELECTED_MODIFIER,
  }),
  setModifierGroupNameTranslation: (
    modifierGroupID: string,
    value?: TranslationDetail,
  ) => ({
    type: actions.SET_MODIFIER_GROUP_NAME_TRANSLATION,
    payload: { value, modifierGroupID },
  }),
  setModifierNameTranslation: (value: Modifier[]) => ({
    type: actions.SET_MODIFIER_NAME_TRANSLATION,
    payload: { value },
  }),
  removeTranslationError: (value?: string) => ({
    type: actions.REMOVE_TRANSLATION_ERROR,
    payload: { value },
  }),
  syncSearchItems: (items?: Item[], deletedItemIDs?: string[]) => ({
    type: actions.SYNC_SEARCH_ITEMS,
    payload: { items, deletedItemIDs },
  }),
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

let tmpModifierGroupsForSearch: ModifierGroup[] = []

const setMenuModifierGroupsFilter = (state: MenuState, menu: Menu) => {
  const newMenu = { ...menu }
  // save tmpModifierGroup for search
  tmpModifierGroupsForSearch = menu.modifierGroups

  // verify if modifier group have been search
  // if so than override modifier group list
  const keyword = state.modifierGroupSearchKeyword
  let filteredModifierGroup: ModifierGroup[] = menu.modifierGroups
  if (keyword?.length > 0) {
    filteredModifierGroup = menu.modifierGroups.filter(mg =>
      mg.modifierGroupName?.toLowerCase().includes(keyword?.toLowerCase()),
    )
    newMenu.modifierGroups = filteredModifierGroup
  }

  return newMenu
}

const handleFetchMenu = (
  state: MenuState,
  action:
    | ActionCreatorTypes["fetchMenu"]
    | ActionCreatorTypes["fetchCatalogue"],
) => {
  const nextState = update(state, {
    merchantID: {
      $set: action.payload.merchantID || "",
    },
    isFetchingMenu: {
      $set: !action.isRefreshing,
    },
  })

  return nextState
}

const handleFetchDepartments = (state: MenuState) => {
  const nextState = update(state, {
    departments: {
      $set: [],
    },
    isFetchingDepartments: {
      $set: true,
    },
    subDepartments: {
      $set: [],
    },
  })
  return nextState
}

const handleFetchDepartmentsFail = (state: MenuState) =>
  update(state, {
    isFetchingDepartments: { $set: false },
  })

const handleFetchMenuFail = (state: MenuState) => {
  const nextState = update(state, {
    isFetchingMenu: { $set: true },
    isEditAllowed: {
      $set: false,
    },
    isCreateItemAllowed: {
      $set: false,
    },
    isStatusChangeAllowed: {
      $set: false,
    },
  })

  return nextState
}

const handleFetchMenuSuccess = (
  state: MenuState,
  action: AxiosAction<FetchMenuResponse>,
) => {
  const { isSellingTimeStructure } = state

  let menu: Menu = action.payload?.data ?? initialMenu()

  menu = setMenuModifierGroupsFilter(state, menu)
  let { section } = state.overview
  const { sellingTime } = state.overview

  if (!isSellingTimeStructure) {
    const sections = orderBy(menu?.sections ?? [], ["sortOrder"], ["desc"])

    menu.sections = sections // update sort order

    section =
      find(
        menu?.sections,
        ({ sectionID }) => sectionID === section?.sectionID,
      ) ?? sections[0]
  }

  // Add default value for attributesData
  menu.attributesData = menu.attributesData ?? {}

  let nextState = update(state, {
    isFetchingMenu: { $set: false },
  })

  nextState = update(nextState, {
    menu: {
      $set: menu,
    },
    sellingTimes: {
      $set: getSellingTimeList(menu),
    },
    categories: {
      $set: getCategoryList(menu),
    },
    items: {
      $set: state.isMartDepartmentStructure
        ? getMartItemList(menu)
        : getItemList(menu),
    },
    subDepartments: {
      $set: getMartSubDepartmentList(menu),
    },
  })

  if (!isSellingTimeStructure && section) {
    nextState = handleSelectOverviewSection(nextState, section)
  }
  if (isSellingTimeStructure) {
    nextState = handleSelectOverviewSellingTime(nextState, sellingTime)
  }

  nextState = handleUpdateEditAllowed(nextState)

  return nextState
}

const handleFetchDepartmentsSuccess = (
  state: MenuState,
  action: AxiosAction<FetchDepartmentsResponse>,
) => {
  const itemClasses = action?.payload?.data?.itemClasses ?? []
  const isMore = action.payload.data.more

  const departments = itemClasses.map(mapItemClassToDepartment)

  const nextState = update(state, {
    departments: {
      $set: departments,
    },
    isMoreDepartments: { $set: isMore },
    isFetchingDepartments: { $set: false },
    drawer: {
      itemSubDepartments: {
        $set: {},
      },
    },
  })

  return nextState
}

const handleFetchSubDepartmentsSuccess = (
  state: MenuState,
  action: AxiosAction<
    FetchDepartmentsResponse,
    ActionCreatorTypes["fetchSubDepartments"]
  >,
) => {
  const itemClasses = action?.payload?.data?.itemClasses ?? []

  let newItemSubDepartments = {}

  if (state.drawer.itemSubDepartments) {
    newItemSubDepartments = {
      ...state.drawer.itemSubDepartments,
    }
  }

  newItemSubDepartments = {
    ...newItemSubDepartments,
    [action.meta.previousAction.payload.departmentID]: itemClasses,
  }

  const nextState = update(state, {
    drawer: {
      itemSubDepartments: {
        $set: newItemSubDepartments,
      },
    },
  })

  return nextState
}

const updateOriginalTranslationFromDSMap = (
  state: MenuState,
  action: AxiosAction<
    FetchTranslationResponse,
    ActionCreatorTypes["getTranslation"]
  >,
) => {
  const { textTranslation } = action.payload.data
  const { params } = action.meta.previousAction

  const key = generateTranslationFromDSKey(params)
  const updatedValue = merge(state.originalTranslationFromDSMap, {
    [key]: { ...textTranslation },
  })

  return update(state, {
    originalTranslationFromDSMap: { $set: updatedValue },
  })
}

const handleUpdateExistingTranslationSuccess = (
  state: MenuState,
  action: AxiosAction<
    FetchTranslationResponse,
    ActionCreatorTypes["updateExistingTranslation"]
  >,
) => {
  const { textTranslation } = action.payload.data
  const { params } = action.meta.previousAction
  switch (params.entity) {
    case "item":
      if (state.drawer.item) {
        if (params.textType === "description") {
          const finalTranslation = {
            ...textTranslation,
            ...state.drawer.item.descriptionTranslation?.translation,
          }
          const translation: TranslationSetting = {
            translation: finalTranslation,
            originalText: params.text,
          }
          return update(state, {
            drawer: {
              item: {
                descriptionTranslation: { $set: translation },
              },
            },
          })
        }
        const finalTranslation = {
          ...textTranslation,
          ...state.drawer.item.nameTranslation?.translation,
        }
        const translation: TranslationSetting = {
          translation: finalTranslation,
          originalText: params.text,
        }
        return update(state, {
          drawer: {
            item: {
              nameTranslation: { $set: translation },
            },
          },
        })
      }
      return state
    case "category":
      if (state.drawer.category) {
        const finalTranslation = {
          ...textTranslation,
          ...state.drawer.category.nameTranslation?.translation,
        }
        const translation: TranslationSetting = {
          translation: finalTranslation,
          originalText: params.text,
        }
        return update(state, {
          drawer: {
            category: {
              nameTranslation: { $set: translation },
            },
          },
        })
      }
      return state
    case "modifierGroup":
      if (state.menu.modifierGroups) {
        const objectUpdated = state.menu.modifierGroups.find(
          obj => obj.modifierGroupID === params.destinationKey,
        )
        if (objectUpdated) {
          const finalTranslation = {
            ...textTranslation,
            ...objectUpdated.nameTranslation?.translation,
          }
          const translation: TranslationSetting = {
            translation: finalTranslation,
            originalText: params.text,
          }
          const newObject = {
            ...objectUpdated,
            nameTranslation: translation,
          }
          return update(state, {
            menu: {
              modifierGroupSelected: { $set: newObject },
            },
          })
        }
      }
      return state
    case "modifier":
      if (state.menu.modifierGroups) {
        const destinationUpdate = params.destinationKey?.split(",") || []
        const modifierUpdated =
          state.menu.modifierSelected ||
          state.menu.modifierGroups.find(
            obj => obj.modifierGroupID === destinationUpdate[0],
          )?.modifiers

        const modifierLocation =
          modifierUpdated?.findIndex(
            obj => obj.modifierID === destinationUpdate[1],
          ) ?? -1

        if (modifierUpdated && modifierLocation >= 0) {
          const finalValue = modifierUpdated.map((value, index) =>
            modifierLocation === index
              ? {
                  ...value,
                  nameTranslation: {
                    translation: {
                      ...textTranslation,
                      ...value.nameTranslation?.translation,
                    },
                    originalText: params.text,
                  },
                }
              : value,
          )
          return update(state, {
            menu: {
              modifierSelected: { $set: finalValue },
            },
          })
        }
      }
      return state
    default:
      return state
  }
}

const handleFetchTranslationSuccess = (
  state: MenuState,
  action: AxiosAction<
    FetchTranslationResponse,
    ActionCreatorTypes["getTranslation"]
  >,
  actionType: string,
) => {
  const { textTranslation } = action.payload.data
  const { params, keys } = action.meta.previousAction
  const translation: TranslationSetting = {
    translation: textTranslation,
    originalText: params.text,
  }
  switch (params.entity) {
    case "item":
      if (state.drawer.item) {
        const translationError = new Map(state.drawer.item.translationError)
        translationError.delete(
          getItemTranslationErrorKey(params.textType, params.text),
        )

        if (params.textType === "description") {
          if (!translation.translation) {
            translation.translation = Object.fromEntries(
              params.textTargetLang?.map(lang => [lang, ""]) ?? [],
            )
          }

          return update(state, {
            drawer: {
              item: {
                isDescriptionTranslating: { $set: false },
                translationError: { $set: translationError },
                resetNameTranslation: { $set: undefined },
                ...(actionType === actions.GET_TRANSLATION
                  ? { descriptionTranslation: { $set: translation } }
                  : { resetDescriptionTranslation: { $set: translation } }),
              },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        return update(state, {
          drawer: {
            item: {
              isNameTranslating: { $set: false },
              translationError: { $set: translationError },
              resetDescriptionTranslation: { $set: undefined },
              ...(actionType === actions.GET_TRANSLATION
                ? { nameTranslation: { $set: translation } }
                : { resetNameTranslation: { $set: translation } }),
            },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "category":
      if (state.drawer.category) {
        const translationError = new Map(state.drawer.category.translationError)
        translationError.delete(params.text)

        return update(state, {
          drawer: {
            category: {
              isNameTranslating: { $set: false },
              translationError: { $set: translationError },
              ...(actionType === actions.GET_TRANSLATION
                ? { nameTranslation: { $set: translation } }
                : { resetNameTranslation: { $set: translation } }),
            },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "modifierGroup":
      if (state.menu.modifierGroups) {
        const objectUpdated =
          state.menu.modifierGroupSelected ||
          state.menu.modifierGroups.find(
            obj => obj.modifierGroupID === params.destinationKey,
          )
        if (objectUpdated) {
          if (actionType === actions.GET_TRANSLATION) {
            const newObject = {
              ...objectUpdated,
              nameTranslation: translation,
              translationError: new Map(objectUpdated.translationError),
            }
            newObject.translationError.delete(params.text)

            return update(state, {
              menu: {
                modifierGroupSelected: { $set: newObject },
              },
              autoTranslationQueue: { $remove: keys ?? [] },
            })
          }
          const newObject = {
            ...objectUpdated,
            resetNameTranslation: translation,
            translationError: new Map(objectUpdated.translationError),
          }
          newObject.translationError.delete(params.text)

          return update(state, {
            menu: {
              modifierGroupSelected: { $set: newObject },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        if (actionType === actions.GET_TRANSLATION) {
          const newObject: ModifierGroup = {
            ...emptyModifierGroup,
            nameTranslation: translation,
          }
          return update(state, {
            menu: {
              modifierGroupSelected: { $set: newObject },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        const newObject = {
          ...emptyModifierGroup,
          resetNameTranslation: translation,
        }
        return update(state, {
          menu: {
            modifierGroupSelected: { $set: newObject },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "modifier":
      if (state.menu.modifierGroups) {
        const destinationUpdate = params.destinationKey?.split(",") || []
        const modifierUpdated =
          state.menu.modifierSelected ||
          state.menu.modifierGroups.find(
            obj => obj.modifierGroupID === destinationUpdate[0],
          )?.modifiers

        const modifierLocation =
          modifierUpdated?.findIndex(
            obj => obj.modifierID === destinationUpdate[1],
          ) ?? -1

        if (modifierUpdated && modifierLocation >= 0) {
          if (actionType === actions.GET_TRANSLATION) {
            const finalValue = modifierUpdated.map((value, index) =>
              modifierLocation === index
                ? { ...value, nameTranslation: translation }
                : value,
            )
            return update(state, {
              menu: {
                modifierSelected: { $set: finalValue },
              },
              autoTranslationQueue: { $remove: keys ?? [] },
            })
          }
          const finalValue = modifierUpdated.map((value, index) =>
            modifierLocation === index
              ? { ...value, resetNameTranslation: translation }
              : value,
          )
          return update(state, {
            menu: {
              modifierSelected: { $set: finalValue },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        const newModifier: Modifier = {
          ...initialModifiers[0],
          modifierID: destinationUpdate[1],
          ...(actionType === actions.GET_TRANSLATION
            ? { nameTranslation: translation }
            : { resetNameTranslation: translation }),
        }
        if (modifierUpdated) {
          const newModifierGroup = [...modifierUpdated, newModifier]
          return update(state, {
            menu: {
              modifierSelected: { $set: newModifierGroup },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        const existingModifier =
          state.menu.modifierSelected &&
          state.menu.modifierSelected?.find(
            v => v.modifierID === destinationUpdate[1],
          )
        if (existingModifier) {
          const finalModifier = state.menu.modifierSelected?.map(value =>
            newModifier.modifierID === value.modifierID
              ? { ...newModifier, nameTranslation: value.nameTranslation }
              : value,
          )
          return update(state, {
            menu: {
              modifierSelected: { $set: finalModifier },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        return update(state, {
          menu: {
            modifierSelected: {
              $set: state.menu.modifierSelected
                ? [...state.menu.modifierSelected, newModifier]
                : [newModifier],
            },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    default:
      return state
  }
}

const handleFetchTranslation = (
  state: MenuState,
  action: ActionCreatorTypes["getTranslation"],
) => {
  const { params, keys } = action
  switch (params.entity) {
    case "item":
      if (state.drawer.item) {
        if (params.textType === "description") {
          return update(state, {
            drawer: {
              item: {
                isDescriptionTranslating: { $set: true },
              },
            },
            autoTranslationQueue: { $add: keys ?? [] },
          })
        }
        return update(state, {
          drawer: {
            item: {
              isNameTranslating: { $set: true },
            },
          },
          autoTranslationQueue: { $add: keys ?? [] },
        })
      }
      return state
    case "category":
      if (state.drawer.category) {
        return update(state, {
          drawer: {
            category: {
              isNameTranslating: { $set: true },
            },
          },
          autoTranslationQueue: { $add: keys ?? [] },
        })
      }
      return state
    case "modifierGroup":
    case "modifier":
      return update(state, {
        autoTranslationQueue: { $add: keys ?? [] },
      })
    default:
      return state
  }
}

const handleFetchTranslationFail = (
  state: MenuState,
  action: AxiosAction<
    FetchTranslationResponse,
    ActionCreatorTypes["getTranslation"]
  >,
) => {
  const { params, keys } = action.meta.previousAction
  const data = action.error?.response?.data
  const failedResponse: TranslationFailedResponse = {
    message: data?.message,
    target: data?.target || "",
  }

  switch (params.entity) {
    case "item":
      if (state.drawer.item) {
        const translationError = new Map(state.drawer.item.translationError)
        translationError.set(
          getItemTranslationErrorKey(params.textType, params.text),
          failedResponse,
        )

        return update(state, {
          drawer: {
            item: {
              isNameTranslating: { $set: false },
              isDescriptionTranslating: { $set: false },
              translationError: { $set: translationError },
            },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "category":
      if (state.drawer.category) {
        const translationError = new Map(state.drawer.category.translationError)
        translationError.set(params.text, failedResponse)

        return update(state, {
          drawer: {
            category: {
              isNameTranslating: { $set: false },
              translationError: { $set: translationError },
            },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "modifierGroup":
      if (state.menu.modifierGroups) {
        const objectUpdated =
          state.menu.modifierGroupSelected ||
          state.menu.modifierGroups.find(
            obj => obj.modifierGroupID === params.destinationKey,
          )
        if (objectUpdated) {
          const newObject = {
            ...objectUpdated,
            isNameTranslating: false,
            translationError: new Map(objectUpdated.translationError),
          }
          newObject.translationError.set(params.text, failedResponse)

          return update(state, {
            menu: {
              modifierGroupSelected: { $set: newObject },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        const newObject = {
          ...emptyModifierGroup,
          isNameTranslating: false,
          translationError: new Map([[params.text, failedResponse]]),
        }
        return update(state, {
          menu: {
            modifierGroupSelected: { $set: newObject },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    case "modifier":
      if (state.menu.modifierGroups) {
        const destinationUpdate = params.destinationKey?.split(",") || []
        const modifierUpdated =
          state.menu.modifierSelected ||
          state.menu.modifierGroups.find(
            obj => obj.modifierGroupID === destinationUpdate[0],
          )?.modifiers
        const modifierLocation =
          modifierUpdated?.findIndex(
            obj => obj.modifierID === destinationUpdate[1],
          ) ?? -1

        if (modifierUpdated && modifierLocation >= 0) {
          const finalValue = modifierUpdated.map((value, index) =>
            modifierLocation === index
              ? {
                  ...value,
                  isNameTranslating: false,
                  translationError: new Map([
                    ...(value.translationError ?? []),
                    [params.text, failedResponse],
                  ]),
                }
              : value,
          )
          return update(state, {
            menu: {
              modifierSelected: { $set: finalValue },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        const newModifier: Modifier = {
          ...initialModifiers[0],
          modifierID: destinationUpdate[1],
          isNameTranslating: false,
          translationError: new Map([[params.text, failedResponse]]),
        }
        if (modifierUpdated) {
          const newModifierGroup = [...modifierUpdated, newModifier]
          return update(state, {
            menu: {
              modifierSelected: { $set: newModifierGroup },
            },
            autoTranslationQueue: { $remove: keys ?? [] },
          })
        }
        return update(state, {
          menu: {
            modifierSelected: { $set: [newModifier] },
          },
          autoTranslationQueue: { $remove: keys ?? [] },
        })
      }
      return state
    default:
      return state
  }
}

const actionSuccess = (action: string) => `${action}_SUCCESS`

const actionFail = (action: string) => `${action}_FAIL`

// reducer
export const reducer = createReducer<MenuState, string, ActionCreatorTypeList>(
  initialState(),
  {
    [actions.ITEM_FILTER]: (
      state: MenuState,
      action: ActionCreatorTypes["itemFilter"],
    ) => {
      const nextState = update(state, {
        itemFilter: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.TOGGLE_ITEM_PHOTO_COVERAGE_BANNER]: (
      state: MenuState,
      action: ActionCreatorTypes["toggleItemPhotoCoverageBanner"],
    ) => {
      const nextState = update(state, {
        isItemPhotoCoverageBannerSkipped: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.CLEAR_MENU]: (state: MenuState) => {
      const { drawer, overview, itemFilter, menu } = initialState()
      const nextState = update(state, {
        drawer: {
          $set: drawer,
        },
        overview: {
          $set: overview,
        },
        itemFilter: {
          $set: itemFilter,
        },
        menu: {
          $set: menu,
        },
        enablePartialMenu: {
          $set: false,
        },
        // Fail closed on menu (group) switch: reset the menu-group BCRS flag so a
        // previous group's value can't leak while the next group's getGrabXValue
        // is still pending (BP-55344 review).
        enableBcrsMenuGroupV2: {
          $set: false,
        },
        isMenuGroupPage: {
          $set: false,
        },
      })

      return nextState
    },
    [actions.CLEAR_STORE]: (state: MenuState) => {
      const nextState = update(state, {
        merchantID: {
          $set: "",
        },
        outlet: {
          $set: undefined,
        },
      })
      return nextState
    },
    [actions.FETCH_MENU]: handleFetchMenu,
    [actions.FETCH_CATALOGUE]: handleFetchMenu,
    [actions.FETCH_DEPARTMENTS]: handleFetchDepartments,
    [actionFail(actions.FETCH_MENU)]: handleFetchMenuFail,
    [actionFail(actions.FETCH_CATALOGUE)]: handleFetchMenuFail,
    [actionFail(actions.FETCH_DEPARTMENTS)]: handleFetchDepartmentsFail,
    [actionSuccess(actions.FETCH_MENU)]: handleFetchMenuSuccess,
    [actionSuccess(actions.FETCH_CATALOGUE)]: handleFetchMenuSuccess,
    [actionSuccess(actions.FETCH_DEPARTMENTS)]: handleFetchDepartmentsSuccess,
    [actionSuccess(
      actions.FETCH_SUBDEPARTMENTS,
    )]: handleFetchSubDepartmentsSuccess,
    [actions.FILTER_ITEM_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["filterItemID"],
    ) => {
      const nextState = update(state, {
        overview: {
          itemID: { $set: action.payload.itemID },
        },
      })
      return nextState
    },
    [actions.SET_SEARCH_TEXT]: (
      state: MenuState,
      action: ActionCreatorTypes["setSearchText"],
    ) => {
      const nextState = update(state, {
        overview: {
          search: { $set: action.payload.searchText },
        },
      })
      return nextState
    },
    [actionSuccess(actions.CREATE_SECTION)]: (state: MenuState) => {
      const nextState = update(state, {})
      return nextState
    },
    [actionSuccess(actions.CREATE_CATEGORY)]: (state: MenuState) => {
      const nextState = update(state, {})
      return nextState
    },
    [actions.TOGGLE_DRAWER]: (
      state: MenuState,
      action: ActionCreatorTypes["toggleDrawer"],
    ) => {
      let newDrawer = action?.payload?.drawer
      let isAutoTranslationQueueReset = false
      if (newDrawer.showItem) {
        // if we're showing ItemDrawer, init the linked mod group state
        const linkedModifierGroups: ModifierGroup[] = getLinkedModifierGroups(
          state.menu.modifierGroups,
          newDrawer?.item?.itemID,
          newDrawer?.item?.linkedModifierGroupIDs,
        )
        newDrawer = {
          ...newDrawer,
          linkedModifierGroupsDrawerState: {
            linkedModifierGroups,
            hasChanged: false,
            hasBeenRearranged: false,
          },
        }
        isAutoTranslationQueueReset = true
      }
      if (newDrawer.showCategory) {
        isAutoTranslationQueueReset = true
      }
      const nextState = update(state, {
        drawer: {
          $merge: newDrawer,
        },
        ...(isAutoTranslationQueueReset && {
          autoTranslationQueue: { $set: new Set<string>() },
          originalTranslationFromDSMap: { $set: {} },
        }),
        ...(newDrawer.showItem && {
          alcoholPrediction: { $set: undefined },
          tobaccoPrediction: { $set: undefined },
        }),
      })
      return nextState
    },
    [actions.TOGGLE_MODAL]: (
      state: MenuState,
      action: ActionCreatorTypes["toggleModal"],
    ) => {
      const newModal = action?.payload?.modal

      const nextState = update(state, {
        modal: {
          $merge: newModal,
        },
      })
      return nextState
    },
    [actions.SELECT_SECTION]: (
      state: MenuState,
      action: ActionCreatorTypes["selectSection"],
    ) => {
      const section = action?.payload?.section
      const nextState = handleSelectOverviewSection(state, section)
      return nextState
    },
    [actions.SELECT_SELLING_TIME]: (
      state: MenuState,
      action: ActionCreatorTypes["selectSellingTime"],
    ) => {
      const sellingTime = action?.payload?.sellingTime
      const nextState = handleSelectOverviewSellingTime(
        state,
        sellingTime || null,
      )
      return nextState
    },
    [actions.SELECT_CATEGORY]: (
      state: MenuState,
      action: ActionCreatorTypes["selectCategory"],
    ) => {
      const category = action.payload.category

      let nextState = state

      if (!state.isSellingTimeStructure) {
        const section = find(
          state.menu.sections,
          s => s.sectionID === category?.sectionID,
        )

        nextState = section
          ? handleSelectOverviewSection(state, section)
          : state
      }

      nextState = category
        ? handleSelectOverviewCategory(nextState, category)
        : state
      return nextState
    },
    [actions.SELECT_DEPARTMENT]: (
      state: MenuState,
      action: ActionCreatorTypes["selectDepartment"],
    ) => {
      const { department } = action.payload

      let nextState = state

      if (!state.isSellingTimeStructure) {
        const section = find(
          state.menu.sections,
          s => s.sectionID === department?.sectionID,
        )

        nextState = section
          ? handleSelectOverviewSection(state, section)
          : state
      }

      nextState = department
        ? handleSelectOverviewDepartment(nextState, department)
        : state
      return nextState
    },
    [actions.SELECT_SUB_DEPARTMENT]: (
      state: MenuState,
      action: ActionCreatorTypes["selectSubDepartment"],
    ) => {
      const { subDepartment } = action.payload

      let nextState = state
      nextState = subDepartment
        ? handleSelectOverviewSubDepartment(nextState, subDepartment)
        : state
      return nextState
    },
    [actions.SELECT_SECTION_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["selectSectionByID"],
    ) => {
      const sectionID = action?.payload?.sectionID
      return handleSelectSectionByID(state, sectionID)
    },
    [actions.SELECT_CATEGORY_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["selectCategoryByID"],
    ) => {
      const categoryID = action.payload.categoryID
      return handleSelectCategoryByID(state, categoryID)
    },
    [actions.SELECT_DEPARTMENT_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["selectDepartmentByID"],
    ) => {
      const departmentID = action.payload.departmentID
      return handleSelectDepartmentByID(state, departmentID)
    },
    [actions.SELECT_SUB_DEPARTMENT_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["selectSubDepartmentByID"],
    ) => {
      const subDepartmentID = action.payload.subDepartmentID
      return handleSelectSubDepartmentByID(state, subDepartmentID)
    },
    [actions.SELECT_ITEM_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["selectItemByID"],
    ) => {
      const itemID = action.payload.itemID
      return handleSelectItemByID(state, itemID)
    },
    [actions.MULTI_SELECT_DEPARTMENT]: (
      state: MenuState,
      action: ActionCreatorTypes["multiSelectDepartment"],
    ) => {
      const { department, value } = action.payload
      return handleMultiSelectDepartment(state, department, value)
    },
    [actions.MULTI_SELECT_SUB_DEPARTMENT]: (
      state: MenuState,
      action: ActionCreatorTypes["multiSelectSubDepartment"],
    ) => {
      const { subDepartment, value } = action.payload
      return handleMultiSelectSubDepartment(state, subDepartment, value)
    },
    [actions.MULTI_SELECT_CATEGORY]: (
      state: MenuState,
      action: ActionCreatorTypes["multiSelectCategory"],
    ) => {
      const { category, value } = action.payload
      return handleMultiSelectCategory(state, category, value)
    },
    [actions.MULTI_SELECT_ITEM]: (
      state: MenuState,
      action: ActionCreatorTypes["multiSelectItem"],
    ) => {
      const { item, value } = action.payload
      const nextState = handleMultiSelectItems(state, [item], value)
      return nextState
    },
    [actions.SYNC_SEARCH_ITEMS]: (
      state: MenuState,
      action: ActionCreatorTypes["syncSearchItems"],
    ) => {
      // Handle sync delete items
      if (action.payload.deletedItemIDs) {
        const deletedIDsSet = new Set(action.payload.deletedItemIDs)

        return update(state, {
          items: {
            $set: state.items.filter(
              item => !deletedIDsSet.has(item.itemID ?? ""),
            ),
          },
          ...(state.searchResults && {
            searchResults: {
              $set: state.searchResults.filter(
                item => !deletedIDsSet.has(item.itemID ?? ""),
              ),
            },
          }),
        })
      }

      // Handle sync update items
      const updatedItemsMap = new Map(
        compact(action.payload.items).map(item => [item.itemID, item]),
      )

      if (updatedItemsMap.size === 0) {
        return state
      }

      const updateItemsArray = (items: Item[]): Item[] =>
        items.map(item => {
          const updatedItem = updatedItemsMap.get(item.itemID ?? "")
          if (!updatedItem) return item

          return {
            ...item,
            // Only pick specific fields to update
            availableStatus: unifyHiddenStatus(updatedItem.availableStatus),
            availableAt: updatedItem.availableAt,
          }
        })

      return update(state, {
        items: { $set: updateItemsArray(state.items) },
        ...(state.searchResults && {
          searchResults: { $set: updateItemsArray(state.searchResults) },
        }),
      })
    },
    [actions.SEARCH_MODIFIER_GROUP]: (
      state: MenuState,
      action: ActionCreatorTypes["searchModifierGroup"],
    ) => {
      const keyword = action?.payload?.keyword
      let filteredModifierGroup: ModifierGroup[] = tmpModifierGroupsForSearch

      filteredModifierGroup = tmpModifierGroupsForSearch.filter(mg =>
        mg.modifierGroupName?.toLowerCase().includes(keyword?.toLowerCase()),
      )
      if (keyword?.length <= 0) {
        filteredModifierGroup = tmpModifierGroupsForSearch
      }
      return update(state, {
        menu: {
          modifierGroups: {
            $set: filteredModifierGroup,
          },
        },
        modifierGroupSearchKeyword: {
          $set: keyword,
        },
      })
    },
    [actions.SEARCH_MODIFIER_GROUP_BY_ID]: (
      state: MenuState,
      action: ActionCreatorTypes["searchModifierGroupByID"],
    ) => {
      const modifierGroupID = action?.payload?.modifierGroupID
      let filteredModifierGroup: ModifierGroup[] = tmpModifierGroupsForSearch

      filteredModifierGroup = tmpModifierGroupsForSearch.filter(
        mg => mg.modifierGroupID === modifierGroupID,
      )
      return update(state, {
        menu: {
          modifierGroups: {
            $set: filteredModifierGroup,
          },
        },
      })
    },
    [actions.FETCH_STORE_LIST]: (
      state: MenuState,
      action: ActionCreatorTypes["fetchStoreList"],
    ) => {
      if (action.payload.append) return state

      return update(state, {
        outlets: {
          $set: undefined,
        },
        isFetchingOutlets: { $set: true },
        stopAfterFirstPage: { $set: action.payload.stopAfterFirstPage },
      })
    },
    [actionSuccess(actions.FETCH_STORE_LIST)]: (
      state: MenuState,
      action: AxiosAction<
        FetchStoreListResponse,
        ActionCreatorTypes["fetchStoreList"]
      >,
    ) => {
      const stopAfterFirstPage = !!action?.meta?.previousAction?.payload
        ?.stopAfterFirstPage
      const data = action?.payload?.data
      const merchants = data?.merchants
      const hasMore = !!data?.metadata?.hasMore
      const filteredMerchants = merchants?.filter(isMenuStoreVisible) || []
      const outletsUpdateSpec = !state.outlets
        ? {
            $set: filteredMerchants,
          }
        : {
            $push: filteredMerchants,
          }

      if (!merchants?.length) {
        // Track empty outlets
        TroyWebTracker.sendEvent({
          category: EventCategory.endpoint,
          action: EventAction.emptyOutlets,
          labels: {
            ENDPOINT: action?.meta?.previousAction?.payload?.request?.url || "",
            REQUEST_ID:
              action?.payload?.headers?.["x-grabkit-grab-requestid"] || "",
            HAS_MORE: hasMore,
          },
        })
      }

      return update(state, {
        outlets: outletsUpdateSpec,
        isFetchingOutlets: { $set: stopAfterFirstPage ? false : hasMore },
        outletsHasMore: { $set: hasMore },
        isMenuGroup: {
          $set:
            !!data?.menuGroupSetting?.isEnableMenuGroup ||
            !!data?.menuGroupSetting?.isEnableUCS,
        },
        isUCS: {
          $set: !!data?.menuGroupSetting?.isEnableUCS,
        },
        isSellingTimeStructure: {
          $set: !!data?.menuGroupSetting?.isEnableSellingTime,
        },
        isMenuGroupServiceBasedMenuEnabled: {
          $set: !!data?.menuGroupSetting?.isEnableServiceBasedMenu,
        },
        isDepartmentTaggingMenuGroupEnabled: {
          $set: !!data?.menuGroupSetting?.isEnableDepartmentTaggingEnhancement,
        },
        isEnableAttributes: {
          $set: !!data?.menuGroupSetting?.isEnableAttributes,
        },
        isEnableFreeTextItemAttribute: {
          $set: !!data?.menuGroupSetting?.isEnableFreeTextItemAttribute,
        },
        isEnableDecimalItemAttribute: {
          $set: !!data?.menuGroupSetting?.isEnableDecimalItemAttribute,
        },
        merchantGroupID: {
          $set: data?.merchantGroupID || "",
        },
      })
    },
    [actionFail(actions.FETCH_STORE_LIST)]: (state: MenuState) => {
      return update(state, {
        outlets: {
          $apply: (prev: MerchantStoreInfo[] | undefined) => {
            if (!prev) {
              return []
            }
            return prev
          },
        },
        outletsHasMore: { $set: false },
        isFetchingOutlets: { $set: false },
        isMenuGroup: { $set: false },
        isUCS: { $set: false },
        merchantGroupID: { $set: "" },
      })
    },
    [actions.FETCH_MERCHANT_DETAIL]: (
      state: MenuState,
      action: ActionCreatorTypes["fetchMerchantDetail"],
    ) => {
      const nextState = update(state, {
        merchantID: {
          $set: action.payload.merchantID,
        },
        outletV2: {
          $set: null,
        },
        isMartStore: {
          $set: false,
        },
        isMartDepartmentStructure: {
          $set: false,
        },
      })

      return nextState
    },
    [actionSuccess(actions.FETCH_MERCHANT_DETAIL)]: (
      state: MenuState,
      action: AxiosAction<FetchMerchantDetailResponse>,
    ) => {
      const { merchant } = action?.payload?.data
      TroyWebTracker.setIsCatalogue(isMartStoreV2(merchant))
      return update(state, {
        outletV2: {
          $set: merchant,
        },
        isMartStore: {
          $set: isMartStoreV2(merchant),
        },
        isMartDepartmentStructure: {
          $set: isMartDepartmentV2Structure(merchant),
        },
        isSellingTimeStructure: {
          $set: getIsSellingTimeStructureV2(merchant),
        },
      })
    },
    [actions.INIT_LINKED_MODIFIERGROUP]: (
      state: MenuState,
      action: ActionCreatorTypes["initLinkedModifierGroup"],
    ) => {
      return update(state, {
        drawer: {
          linkedModifierGroupsDrawerState: {
            linkedModifierGroups: {
              $set: action?.payload?.modifierGroups,
            },
            hasChanged: {
              $set: false,
            },
            hasBeenRearranged: {
              $set: false,
            },
          },
        },
      })
    },
    [actions.UPDATE_LINKED_MODIFIERGROUP]: (
      state: MenuState,
      action: ActionCreatorTypes["updateLinkedModifierGroup"],
    ) => {
      return update(state, {
        drawer: {
          linkedModifierGroupsDrawerState: {
            linkedModifierGroups: {
              $set: action?.payload?.modifierGroups,
            },
            hasChanged: {
              $set: true,
            },
          },
        },
      })
    },
    [actions.REARRANGE_LINKED_MODIFIERGROUP]: (
      state: MenuState,
      action: ActionCreatorTypes["rearrangeLinkedModifierGroup"],
    ) => {
      return update(state, {
        drawer: {
          linkedModifierGroupsDrawerState: {
            linkedModifierGroups: {
              $set: action?.payload?.modifierGroups,
            },
            hasBeenRearranged: {
              $set: true,
            },
          },
        },
      })
    },
    [actions.FETCH_LIST_BRANDS_SEARCH]: (
      state: MenuState,
      action: ActionCreatorTypes["fetchListBrandsSearch"],
    ) => {
      const emptyDataSpec = !state.brands.isLoading
        ? { data: { $set: [] } }
        : {}
      const rtkAction = (action as unknown) as {
        params?: { locale?: string }
      }
      const params = action.payload?.request?.params ?? rtkAction.params

      return update(state, {
        brands: {
          isLoading: { $set: true },
          forLocale: {
            $set: params?.locale,
          },
          ...emptyDataSpec,
        },
      })
    },
    [actions.FETCH_ATTRIBUTE_CLUSTER_OPTION]: (state: MenuState) => {
      return update(state, {
        menu: {
          attributesData: {
            isLoading: {
              $set: true,
            },
          },
        },
      })
    },
    [actions.FETCH_SUPPORTED_ATTRIBUTE_CLUSTER]: (state: MenuState) => {
      return update(state, {
        menu: {
          attributesData: {
            isLoadingSupportedAttributeCluster: {
              $set: true,
            },
          },
        },
      })
    },
    [actionSuccess(actions.FETCH_LIST_BRANDS_SEARCH)]: (
      state: MenuState,
      action: AxiosAction<FetchListItemAttributesResponse>,
    ) => {
      return update(state, {
        brands: {
          isLoading: {
            $set: false,
          },
          data: {
            $set: action.payload.data.attributes ?? [],
          },
          hasMore: {
            $set: action.payload.data.hasMore,
          },
        },
      })
    },
    [actionFail(actions.FETCH_LIST_BRANDS_SEARCH)]: (state: MenuState) => {
      return update(state, {
        brands: {
          isLoading: {
            $set: false,
          },
        },
      })
    },
    [actionSuccess(actions.FETCH_SUPPORTED_ATTRIBUTE_CLUSTER)]: (
      state: MenuState,
      action: AxiosAction<FetchSupportedAttributeClusterResponse>,
    ) => {
      return update(state, {
        menu: {
          attributesData: {
            attributeClusters: {
              $set:
                action.payload.data.supportedAttributeClusterMap
                  ?.attributeClusters,
            },
            isLoadingSupportedAttributeCluster: {
              $set: false,
            },
          },
        },
      })
    },
    [actionSuccess(actions.FETCH_ATTRIBUTE_CLUSTER_OPTION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchListAttributeOptionsResponse,
        ActionCreatorTypes["fetchAttributeClusterOption"]
      >,
    ) => {
      let finalValue
      const attributeClusters: { [s: string]: AttributeCluster } | undefined =
        state.menu.attributesData?.attributeClusters
      if (attributeClusters) {
        const attributeClusterID = action.meta.previousAction.attributeClusterID
        const selectedAttributeCluster =
          attributeClusters?.[(attributeClusterID as unknown) as number]
        if (selectedAttributeCluster) {
          const newSelectedAttributeCluster = {
            ...selectedAttributeCluster,
            attributeClusterOptions: action.payload.data.attributes,
            attributeClusterOptionsHasMore: action.payload.data.hasMore,
          }
          finalValue = {
            ...attributeClusters,
            [attributeClusterID]: newSelectedAttributeCluster,
          }
        } else {
          finalValue = { ...attributeClusters }
        }
        return update(state, {
          menu: {
            attributesData: {
              attributeClusters: {
                $set: finalValue,
              },
              isLoading: {
                $set: false,
              },
            },
          },
        })
      }
      return update(state, {
        menu: {
          attributesData: {
            isLoading: {
              $set: false,
            },
          },
        },
      })
    },
    [actionFail(actions.FETCH_ATTRIBUTE_CLUSTER_OPTION)]: (state: MenuState) =>
      update(state, {
        menu: {
          attributesData: {
            isLoading: { $set: false },
          },
        },
      }),
    [actionSuccess(actions.FETCH_MENU_GROUP_LIST)]: (
      state: MenuState,
      action: AxiosAction<FetchMenuGroupListResponse>,
    ) => {
      const data = action?.payload?.data
      if (!data) return state
      const { menuGroups } = data
      return update(state, {
        menuGroups: { $set: menuGroups || [] },
      })
    },
    [actions.CLEAR_MENU_GROUP]: (state: MenuState) =>
      update(state, {
        menuGroupDetail: {
          $set: initialMenuGroup(),
        },
      }),
    [actions.FETCH_MENU_GROUP_DETAIL]: (state: MenuState) => {
      return update(state, {
        isMartStore: {
          $set: false,
        },
        isMartDepartmentStructure: {
          $set: false,
        },
      })
    },
    [actionSuccess(actions.FETCH_MENU_GROUP_DETAIL)]: (
      state: MenuState,
      action: AxiosAction<{
        menuGroup: MenuGroup
        serviceBasedMenuConfig: ServiceBasedMenuConfig
      }>,
    ) => {
      const menuGroup = action?.payload?.data?.menuGroup ?? initialMenuGroup()
      const serviceBasedMenuConfig =
        action?.payload?.data?.serviceBasedMenuConfig ?? {}
      const isMenuGroupMart = menuGroup.businessType === BusinessType.Shop
      const isMenuGroupMartDepartmentStructure = menuGroup?.menuType === 1
      TroyWebTracker.setIsCatalogue(isMenuGroupMartDepartmentStructure)
      return update(state, {
        menuGroupDetail: {
          $set: menuGroup,
        },
        menuGroupServiceBasedMenuConfig: {
          $set: serviceBasedMenuConfig,
        },
        isMartStore: {
          $set: isMenuGroupMart,
        },
        isMartDepartmentStructure: {
          $set: isMenuGroupMartDepartmentStructure,
        },
      })
    },
    [actions.FETCH_MENU_GROUP_LINKED_STORES]: (
      state: MenuState,
      action: ActionCreatorTypes["fetchMenuGroupLinkedStores"],
    ) => {
      if (action.payload.append) return state
      return update(state, {
        menuGroupLinkedStores: {
          $set: undefined,
        },
        menuGroupLinkedStoresIDs: {
          $set: undefined,
        },
      })
    },
    [actionSuccess(actions.FETCH_MENU_GROUP_LINKED_STORES)]: (
      state: MenuState,
      action: AxiosAction<FetchMenuGroupLinkedStoresResponse>,
    ) => {
      const data = action?.payload?.data
      const storeIDs = data?.storeIDs ?? []
      const outlets = state.outlets ?? []
      const menuGroupLinkedStores = storeIDs.reduce((result, storeID) => {
        const menuGroupLinkedStore = outlets.find(
          (outlet: OutletStore) => outlet.merchantID === storeID,
        )
        return menuGroupLinkedStore
          ? [
              ...result,
              {
                label: menuGroupLinkedStore.merchantName,
                value: menuGroupLinkedStore.merchantID,
              },
            ]
          : result
      }, [])
      return update(state, {
        menuGroupLinkedStores: !state.menuGroupLinkedStores
          ? { $set: menuGroupLinkedStores }
          : { $push: menuGroupLinkedStores },
        menuGroupLinkedStoresIDs: !state.menuGroupLinkedStoresIDs
          ? { $set: storeIDs }
          : { $push: storeIDs },
      })
    },
    [actionFail(actions.FETCH_MENU_GROUP_LINKED_STORES)]: (
      state: MenuState,
    ) => {
      return update(state, {
        menuGroupLinkedStores: {
          $set: undefined,
        },
        menuGroupLinkedStoresIDs: {
          $set: undefined,
        },
      })
    },
    [actions.GET_ITEM_PREDICTION]: (state: MenuState) => {
      return update(state, {
        alcoholPrediction: {
          $set: undefined,
        },
        tobaccoPrediction: {
          $set: undefined,
        },
        alcoholPredictionLoading: {
          $set: true,
        },
        tobaccoPredictionLoading: {
          $set: true,
        },
      })
    },
    [actionSuccess(actions.GET_ITEM_PREDICTION)]: (
      state: MenuState,
      action: AxiosAction<GetAlcoholPredictionResponse>,
    ) => {
      return update(state, {
        alcoholPrediction: {
          $set: action?.payload?.data?.predictions?.isAlcohol,
        },
        tobaccoPrediction: {
          $set: action?.payload?.data?.predictions?.isTobacco,
        },
        alcoholPredictionLoading: {
          $set: false,
        },
        tobaccoPredictionLoading: {
          $set: false,
        },
      })
    },
    [actionFail(actions.GET_ITEM_PREDICTION)]: (state: MenuState) => {
      return update(state, {
        alcoholPrediction: {
          $set: undefined,
        },
        tobaccoPrediction: {
          $set: undefined,
        },
        alcoholPredictionLoading: {
          $set: false,
        },
        tobaccoPredictionLoading: {
          $set: false,
        },
      })
    },
    [actions.FETCH_SUGGESTED_DEPARTMENT]: (state: MenuState) => {
      return update(state, {
        suggestedDepartments: { $set: [] },
        isFetchingDataForGrabCategory: { $set: true },
      })
    },
    [actionSuccess(actions.FETCH_SUGGESTED_DEPARTMENT)]: (
      state: MenuState,
      action: AxiosAction<FetchSuggestedDepartmentResponse>,
    ) => {
      const { suggestedDepartments, predictionID } = action.payload.data
      return update(state, {
        predictionIDGrabCategory: { $set: predictionID },
        suggestedDepartments: { $set: suggestedDepartments ?? [] },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actionFail(actions.FETCH_SUGGESTED_DEPARTMENT)]: (state: MenuState) => {
      return update(state, {
        suggestedDepartments: { $set: [] },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actions.FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY]: (state: MenuState) => {
      return update(state, {
        departmentsForGrabCategory: { $set: [] },
        subDepartmentInDepartmentListForGrabCategory: { $set: [] },
        isFetchingDataForGrabCategory: { $set: true },
      })
    },
    [actionSuccess(actions.FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
      action: AxiosAction<FetchDepartmentsResponse>,
    ) => {
      const { itemClasses, more } = action.payload.data
      const filteredDepartments = filterDepartmentsForGrabCategory(itemClasses)
      const filteredSubDepartments = filteredSubDepartmentsForGrabCategory(
        itemClasses,
      )
      const offset = filteredDepartments?.length
      return update(state, {
        subDepartmentInDepartmentListForGrabCategory: {
          $set: filteredSubDepartments ?? [],
        },
        departmentsForGrabCategory: { $set: filteredDepartments ?? [] },
        isFetchingDataForGrabCategory: { $set: false },
        hasMoreGrabCategory: { $set: more },
        offsetGrabCategory: { $set: offset },
      })
    },
    [actionFail(actions.FETCH_DEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
    ) => {
      return update(state, {
        subDepartmentInDepartmentListForGrabCategory: { $set: [] },
        departmentsForGrabCategory: { $set: [] },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actions.LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY]: (
      state: MenuState,
      action: ActionCreatorTypes["loadMoreDepartmentsForGrabCategory"],
    ) => {
      const isClickingLoadMore = action?.payload?.isLoadingMore
      return update(state, {
        departmentsForGrabCategory: {
          $set: isClickingLoadMore ? state.departmentsForGrabCategory : [],
        },
        subDepartmentInDepartmentListForGrabCategory: {
          $set: isClickingLoadMore
            ? state.subDepartmentInDepartmentListForGrabCategory
            : [],
        },
        isFetchingDataForGrabCategory: { $set: !isClickingLoadMore },
      })
    },
    [actionSuccess(actions.LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
      action: AxiosAction<FetchDepartmentsResponse>,
    ) => {
      const { itemClasses, more } = action.payload.data
      const filteredDepartments = filterDepartmentsForGrabCategory(itemClasses)
      const filteredSubDepartments = filteredSubDepartmentsForGrabCategory(
        itemClasses,
      )
      const departments = state.departmentsForGrabCategory?.concat(
        filteredDepartments,
      )
      const subDepartments = state.subDepartmentInDepartmentListForGrabCategory?.concat(
        filteredSubDepartments,
      )
      const offset = departments?.length

      return update(state, {
        subDepartmentInDepartmentListForGrabCategory: {
          $set: subDepartments ?? [],
        },
        departmentsForGrabCategory: { $set: departments ?? [] },
        isFetchingDataForGrabCategory: { $set: false },
        hasMoreGrabCategory: { $set: more },
        offsetGrabCategory: { $set: offset },
      })
    },
    [actionFail(actions.LOAD_MORE_DEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
    ) => {
      return update(state, {
        subDepartmentInDepartmentListForGrabCategory: {
          $set: state.subDepartmentInDepartmentListForGrabCategory,
        },
        departmentsForGrabCategory: { $set: state.departmentsForGrabCategory },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actions.FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY]: (
      state: MenuState,
      action: ActionCreatorTypes["fetchSubdepartmentsForGrabCategory"],
    ) => {
      const parentName = action.payload.parentName

      return update(state, {
        subDepartmentsForGrabCategory: { $set: [] },
        isFetchingDataForGrabCategory: { $set: true },
        parentName: { $set: parentName },
      })
    },
    [actionSuccess(actions.FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
      action: AxiosAction<FetchDepartmentsResponse>,
    ) => {
      const { itemClasses } = action.payload.data
      return update(state, {
        subDepartmentsForGrabCategory: { $set: itemClasses ?? [] },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actionFail(actions.FETCH_SUBDEPARTMENTS_FOR_GRAB_CATEGORY)]: (
      state: MenuState,
    ) => {
      return update(state, {
        subDepartmentsForGrabCategory: { $set: [] },
        isFetchingDataForGrabCategory: { $set: false },
      })
    },
    [actions.GET_SELECTED_GRAB_CATEGORY]: (
      state: MenuState,
      action: ActionCreatorTypes["getSelectedGrabCategory"],
    ) => {
      const grabCategory = action.payload

      return update(state, {
        selectedGrabCategory: { $set: grabCategory },
      })
    },
    [actionSuccess(actions.GET_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["getTranslation"]
      >,
    ) => {
      const nextState = updateOriginalTranslationFromDSMap(state, action)
      return handleFetchTranslationSuccess(
        nextState,
        action,
        actions.GET_TRANSLATION,
      )
    },
    [actionFail(actions.GET_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["getTranslation"]
      >,
    ) => {
      return handleFetchTranslationFail(state, action)
    },
    [actions.GET_TRANSLATION]: (
      state: MenuState,
      action: ActionCreatorTypes["getTranslation"],
    ) => {
      return handleFetchTranslation(state, action)
    },
    [actionSuccess(actions.GET_SINGLE_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["getTranslation"]
      >,
    ) => {
      const nextState = updateOriginalTranslationFromDSMap(state, action)
      return handleFetchTranslationSuccess(
        nextState,
        action,
        actions.GET_SINGLE_TRANSLATION,
      )
    },
    [actionSuccess(actions.UPDATE_EXISTING_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["updateExistingTranslation"]
      >,
    ) => {
      const nextState = updateOriginalTranslationFromDSMap(state, action)
      return handleUpdateExistingTranslationSuccess(nextState, action)
    },
    [actionFail(actions.UPDATE_EXISTING_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["getTranslation"]
      >,
    ) => {
      return handleFetchTranslationFail(state, action)
    },
    [actionFail(actions.GET_SINGLE_TRANSLATION)]: (
      state: MenuState,
      action: AxiosAction<
        FetchTranslationResponse,
        ActionCreatorTypes["getTranslation"]
      >,
    ) => {
      return handleFetchTranslationFail(state, action)
    },
    [actions.GET_SINGLE_TRANSLATION]: (
      state: MenuState,
      action: ActionCreatorTypes["getTranslation"],
    ) => {
      return handleFetchTranslation(state, action)
    },
    [actions.RESET_AUTO_TRANSLATION_QUEUE]: (state: MenuState) => {
      const nextState = update(state, {
        autoTranslationQueue: { $set: new Set<string>() },
        originalTranslationFromDSMap: { $set: {} },
      })
      return nextState
    },
    [actions.SET_ALCOHOL_SETTING]: (
      state: MenuState,
      action: ActionCreatorTypes["setAlcoholSetting"],
    ) => {
      const nextState = update(state, {
        enableAlcoholSetting: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.SET_BCRS_MENU_GROUP_V2]: (
      state: MenuState,
      action: ActionCreatorTypes["setBcrsMenuGroupV2"],
    ) => {
      const nextState = update(state, {
        enableBcrsMenuGroupV2: {
          $set: action.payload.value,
        },
        // Page mode, not the merchant-group setting: Redux isMenuGroup is also true
        // on the inventory pages of a menu-group-enabled merchant group, and those
        // do populate outletV2 (BP-55344 review).
        isMenuGroupPage: {
          $set: action.payload.isMenuGroupPage,
        },
      })
      return nextState
    },
    [actions.SET_TOBACCO_SETTING]: (
      state: MenuState,
      action: ActionCreatorTypes["setTobaccoSetting"],
    ) => {
      const nextState = update(state, {
        enableTobaccoSetting: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.SET_ENABLE_MENU_TRANSLATION_PORTAL]: (
      state: MenuState,
      action: ActionCreatorTypes["setEnableMenuTranslationPortal"],
    ) => {
      const nextState = update(state, {
        enableMenuTranslationPortal: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.SET_ENABLE_PARTIAL_MENU]: (
      state: MenuState,
      action: ActionCreatorTypes["setEnablePartialMenu"],
    ) => {
      const nextState = update(state, {
        enablePartialMenu: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.SET_ENABLE_GRAB_MEX_PRO_IMAGE_EDITOR]: (
      state: MenuState,
      action: ActionCreatorTypes["setEnableGrabMexProImageEditor"],
    ) => {
      const nextState = update(state, {
        enableGrabMexProImageEditor: {
          $set: action.payload.value,
        },
      })
      return nextState
    },
    [actions.CLEAR_SELECTED_MODIFIER]: (state: MenuState) => {
      return update(state, {
        menu: {
          modifierGroupSelected: {
            $set: undefined,
          },
          modifierSelected: {
            $set: undefined,
          },
        },
      })
    },
    // modifierGroup verify button update the modifierGroupSelected included with removing translationError
    [actions.SET_MODIFIER_GROUP_NAME_TRANSLATION]: (
      state: MenuState,
      action: ActionCreatorTypes["setModifierGroupNameTranslation"],
    ) => {
      const { value, modifierGroupID } = action.payload
      if (state.menu.modifierGroupSelected) {
        const finalValue: ModifierGroup = {
          ...state.menu.modifierGroupSelected,
          nameTranslation: value?.nameTranslation,
          translationError: undefined,
        }
        return update(state, {
          menu: {
            modifierGroupSelected: { $set: finalValue },
          },
        })
      }
      if (state.menu.modifierGroups) {
        const selectedModifierGroup = state.menu.modifierGroups.find(
          x => x.modifierGroupID === modifierGroupID,
        )
        if (selectedModifierGroup) {
          const finalValue: ModifierGroup = {
            ...selectedModifierGroup,
            nameTranslation: value?.nameTranslation,
            translationError: undefined,
          }
          return update(state, {
            menu: {
              modifierGroupSelected: { $set: finalValue },
            },
          })
        }
      }
      return state
    },
    // modifier verify button update the modifierSelected included with removing translationError
    [actions.SET_MODIFIER_NAME_TRANSLATION]: (
      state: MenuState,
      action: ActionCreatorTypes["setModifierNameTranslation"],
    ) => {
      return update(state, {
        menu: {
          modifierSelected: { $set: action.payload.value },
        },
      })
    },
    // item and category verify button update to remove the translationError
    [actions.REMOVE_TRANSLATION_ERROR]: (
      state: MenuState,
      action: ActionCreatorTypes["removeTranslationError"],
    ) => {
      const { value } = action.payload
      switch (value) {
        case "item":
          if (state.drawer.item) {
            return update(state, {
              drawer: {
                item: {
                  translationError: { $set: undefined },
                },
              },
            })
          }
          return state
        case "category":
          if (state.drawer.category) {
            return update(state, {
              drawer: {
                category: {
                  translationError: { $set: undefined },
                },
              },
            })
          }
          return state
        default:
          return state
      }
    },
    [actions.SEARCH_STORE]: (
      state: MenuState,
      action: ActionCreatorTypes["searchOutlets"],
    ) => {
      const isLoadMore = action?.payload?.isLoadMore

      return update(state, {
        ...(isLoadMore
          ? { isFetchingMoreOutletsSearch: { $set: true } }
          : { isFetchingOutletsSearch: { $set: true } }),
      })
    },
    [actionSuccess(actions.SEARCH_STORE)]: (
      state: MenuState,
      action: AxiosAction<
        FetchStoreListResponse & { hasMore: boolean },
        ActionCreatorTypes["searchOutlets"]
      >,
    ) => {
      const merchants = action?.payload?.data?.merchants
      const isLoadMore = !!action?.meta?.previousAction?.payload?.isLoadMore
      const hasMore = !!action?.payload?.data?.hasMore
      const migrationMissingInfoSummary =
        action?.payload?.data?.migrationMissingInfo

      const canPush = isLoadMore && Array.isArray(state.outletsSearch)
      const outletsUpdateSpec = canPush
        ? {
            $push: merchants,
          }
        : {
            $set: merchants,
          }

      const migrationMissingInfoSummaryUpdateSpec = isLoadMore
        ? {
            $set:
              migrationMissingInfoSummary ?? state.migrationMissingInfoSummary,
          }
        : {
            $set: migrationMissingInfoSummary,
          }

      return update(state, {
        outletsSearch: outletsUpdateSpec,
        migrationMissingInfoSummary: migrationMissingInfoSummaryUpdateSpec,
        isFetchingOutletsSearch: { $set: false },
        outletsSearchHasMore: { $set: hasMore },
      })
    },
    [actionFail(actions.SEARCH_STORE)]: (state: MenuState) => {
      return update(state, {
        outletsSearch: {
          $set: undefined,
        },
        migrationMissingInfoSummary: {
          $set: undefined,
        },
        isFetchingOutletsSearch: { $set: false },
      })
    },
    [actions.FETCH_MERCHANT_AGGREGATE]: (state: MenuState) => {
      return update(state, {
        isFetchingMerchantAggregate: { $set: true },
      })
    },
    [actionSuccess(actions.FETCH_MERCHANT_AGGREGATE)]: (
      state: MenuState,
      action: AxiosAction<FetchMerchantAggregateResponse>,
    ) => {
      const data = action?.payload?.data
      const businessTypes = data?.businessTypes
      const deliveryOptions = data?.deliverOptionsOriginal
      const cityList = data?.cities
      return update(state, {
        merchantBusinessTypeList: {
          $set: businessTypes,
        },
        cityList: {
          $set: cityList,
        },
        merchantDeliverOptions: {
          $set: deliveryOptions,
        },
        isFetchingMerchantAggregate: { $set: false },
      })
    },
    [actionFail(actions.FETCH_MERCHANT_AGGREGATE)]: (state: MenuState) => {
      return update(state, {
        merchantBusinessTypeList: {
          $set: undefined,
        },
        cityList: {
          $set: undefined,
        },
        merchantDeliverOptions: {
          $set: undefined,
        },
        isFetchingMerchantAggregate: { $set: false },
      })
    },
    [actionSuccess(actions.FETCH_ITEMS_WITHOUT_PHOTO_COUNT)]: (
      state: MenuState,
      action: AxiosAction<FetchItemsWithoutPhotoCountResponse>,
    ) => {
      const totalItemsWithoutPhoto =
        action?.payload?.data?.totalItemsWithoutPhoto

      return update(state, {
        totalItemsWithoutPhoto: {
          $set: totalItemsWithoutPhoto,
        },
      })
    },
    [actions.SEARCH_ITEMS]: (
      state: MenuState,
      action: ActionCreatorTypes["searchItems"],
    ) => {
      return update(state, {
        searchItemLoading: { $set: action.showLoading ?? true },
      })
    },
    [actionSuccess(actions.SEARCH_ITEMS)]: (
      state: MenuState,
      action: AxiosAction<SearchItemsResponse>,
    ) => {
      const { items, queryID, highlightKey } = action?.payload?.data || {}

      return update(state, {
        searchResults: {
          $set:
            items?.map(item => ({
              ...item,
              availableStatus: unifyHiddenStatus(item.availableStatus),
            })) || [],
        },
        searchItemLoading: { $set: false },
        searchQueryID: { $set: queryID },
        searchHighlightKey: { $set: highlightKey },
      })
    },
    [actionFail(actions.SEARCH_ITEMS)]: (state: MenuState) => {
      return update(state, {
        searchResults: { $set: [] },
        searchItemLoading: { $set: false },
        searchQueryID: { $set: undefined },
        searchHighlightKey: { $set: undefined },
      })
    },
    [actions.CLEAR_SEARCH_RESULTS]: (state: MenuState) => {
      return update(state, {
        searchResults: { $set: undefined },
        searchItemLoading: { $set: false },
        searchQueryID: { $set: undefined },
        searchHighlightKey: { $set: undefined },
      })
    },
  },
)
